import _, { isEmpty, uniq } from "lodash";
import { I18N } from "oc-I18N";
import {
  filterPathIntersectionCached,
  findPathIntersectionCached,
  following_are_optionals,
  sortForSortierung,
} from "oc-app/metadata/GraphMetadata";
import { PathFn, PProxy } from "oc-app/metadata/PathTypes";
import * as Commons from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import { v2, v3 } from "oc-threejs/short3";
import {
  MAuswahl,
  MAuswahlwert,
  MDetailSerie,
  MElement,
  MKeyTable,
  MMass,
  MObjekt,
  MPfosten,
  MProfilkategorie,
  MSerie,
  MTor,
  MUMProfil,
} from "../../metamodell";
import { PAuswahlwert, PElement, PObjekt, PTor } from "../../metamodell_paths";
import { clampBox2Vector3, maxByIfGreaterZero, minMaxBox2 } from "../commons/Utils";
import { Arrangement, getAbschlussleistenAuswahlwerteForElemente } from "./Arrangement";
import { Ecke } from "./Ecke";
import { Pfosten } from "./Pfosten";
import { getHighestScoredMeta, sizeScore } from "./elementScoring";
import {
  getEckadapterInfo,
  getPfostenMetaParameter,
  getPfostenWinkel,
  PfostenMetaParameter,
} from "./pfostenFunctions";
import { Flügel } from "./Flügel";
import { ZElement } from "./ZElement";
import { memoizeFix } from "oc-commons/memoizeMultiMap";
import { getAngleIntervalType } from "../commons/AngleIntervals";
import { Eckadapter } from "./Eckadapter";
import { Empty } from "./Empty";
import { Part } from "./Part";
import { getMMR, UMProfilMetaParamter } from "../RichMetadata";
import { RahmenDef } from "../arrangement3d/schiebetor/types";
import { erzeugeSchiebetorRahmen } from "../arrangement3d/schiebetor/erzeugeSchiebetorRahmen";
import { Box2, Vector2, Vector3 } from "three";

/**
 * FIXME an vielen Stellen steht hier any, das ist aber nur wegen ansonsten zyklischer Imports und sollte mal gerade gezogen werden!
 */

export const sizeApproximatelyEpsilon = 0.06;
export const sizeExactEpsilon = 0.0051;
export const sizeExactEpsilonTore = 0.005; // Torkonfigurator ermöglicht Schrittweiten von 0,5cm; Tor mit 98,5cm muss Masstor sein
export const nullEpsilon = 0.00001;

export enum Richtung {
  LINKS = "Links",
  RECHTS = "Rechts",
}

export const { LINKS, RECHTS } = Richtung;

export function elementOrTor(
  p: PProxy<{ Element?: MElement | MElement[]; Tor?: MTor | MTor[] }>
): PTor {
  return p.proxyUnion(
    (p: any) => p.Element,
    (p: any) => p.Tor
  );
}
window["elementOrTor"] = elementOrTor;

interface SizeRangeExtended extends SizeRange {
  isBreiteKonfigurierbar: boolean;
}

export function getSizeRange(massMetas: MMass[], torMaxBreite: number): SizeRangeExtended {
  const flügelBox = new Box2();
  const Schrittweite = v2();
  const f = (g) => (m: (m: MMass) => number) =>
    Maths.nanToZero(g(massMetas.map(m).filter((x) => x > 0)));
  const min = f(_.min);
  flügelBox.makeEmpty();
  massMetas.forEach((mass) => flügelBox.union(minMaxBox2(mass)));
  flügelBox.max.x = Math.min(flügelBox.max.x, torMaxBreite);
  Schrittweite.x = min((p) => p.Schrittweite?.x ?? 0);
  Schrittweite.y = min((p) => p.Schrittweite?.y ?? 0);
  const isBreiteKonfigurierbar = flügelBox.getSize(v2()).length() > nullEpsilon;
  const isDoppeltor = massMetas.find((m) => m?.Tor?.some((t) => t?.Doppeltor));
  const box = flügelBox.clone();
  if (isDoppeltor) {
    const doppel = (x) => x * 2 + APP.settings.doppeltorFlügelabstand;
    box.min.x = doppel(box.min.x);
    box.max.x = doppel(box.max.x);
  }
  return { box, flügelBox, Schrittweite, isBreiteKonfigurierbar };
}

export function roundSchrittweite(
  axis: "x" | "y",
  axisValue: number,
  box: Box2,
  Schrittweite: Vector2
) {
  if (!hasSchrittweite(axis, Schrittweite)) {
    return Math.min(box.max[axis], axisValue);
  }
  const schrittweite = Schrittweite[axis];
  const min = box.min[axis];
  return Math.round((axisValue - min) / schrittweite) * schrittweite + min;
}

export function hasSchrittweite(axis: "x" | "y", Schrittweite: Vector2): boolean {
  return Schrittweite[axis] > 0.001;
}

export function filterElementMetasForm(elementMeta: MTor[]): MTor[] {
  return filterPathIntersectionCached("elementFunctions.ts:97")(
    elementMeta,
    (element: PTor) => elementOrTor(element.DetailSerie.Serie.DetailSerie),
    elementMeta,
    (element: PTor) => elementOrTor(element.Mass.Form.Mass)
  );
}

export function getElementMetasGleicherForm(form: MObjekt) {
  const elementMetasForm = filterPathIntersectionCached("elementFunctions.ts:106")(
    form,
    (p: PObjekt) => elementOrTor(p.Mass)
  ) as MTor[];
  return elementMetasForm;
}

export function getElementMetasFormFüllung(
  elementMetasForm: MElement[],
  füllungAuswahlwert: MAuswahlwert
) {
  const elementMetasFormFüllung = füllungAuswahlwert
    ? (filterPathIntersectionCached("elementFunctions.ts:115")(
        elementMetasForm,
        (p: PElement) => p,
        füllungAuswahlwert,
        (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt)
      ) as MTor[])
    : [];
  return elementMetasFormFüllung;
}

export function getElementMetaCombinations({
  form,
  flügelSizes,
  füllungAuswahlwert,
}: {
  form: MObjekt;
  flügelSizes?: Vector2[];
  füllungAuswahlwert: MAuswahlwert;
}): {
  elementMetasMassFüllung: MElement[];
  elementMetasMass: MElement[];
  elementMetasFormFüllung: MElement[];
  elementMetasForm: MElement[];
} {
  const elementMetasForm = getElementMetasGleicherForm(form);

  if (elementMetasForm.length == 0) debugger;

  const elementMetasMass =
    isSchiebetorForm(form)
      ? elementMetasForm
      : elementMetasForm.filter((element) =>
          flügelSizes.every(
            (size) =>
              minMaxBox2(element.Mass).distanceToPoint(size) <
              (isTorFn(element) ? sizeExactEpsilonTore : sizeExactEpsilon)
          )
        );
  const elementMetasFormFüllung = getElementMetasFormFüllung(elementMetasForm, füllungAuswahlwert);

  const elementMetasMassFüllung = füllungAuswahlwert
    ? (filterPathIntersectionCached("elementFunctions.ts:149")(
        elementMetasMass,
        (p: PTor) => p,
        füllungAuswahlwert,
        (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt)
      ) as MTor[])
    : [];
  return {
    elementMetasMassFüllung,
    elementMetasMass,
    elementMetasFormFüllung,
    elementMetasForm,
  };
}

export const existsMassElementForFormFüllung = memoizeFix(
  (form: MObjekt, füllungAuswahlwert: MAuswahlwert): boolean => {
    const elementMetasForm = getElementMetasGleicherForm(form);
    const elementMetasFormFüllung = getElementMetasFormFüllung(
      elementMetasForm,
      füllungAuswahlwert
    );
    return elementMetasFormFüllung.some((m: MElement) => m.Mass.Variabel !== "fix");
  }
);

export function isRhombusOderRhombusHolz(elementMeta: MElement): boolean {
  return elementMeta.DetailSerie.some(
    (d) => d.Key == "SYSTEM RHOMBUS" || d.Key == "SYSTEM RHOMBUS HOLZ"
  );
}

export interface SizeRange {
  Schrittweite: Vector2;
  box: Box2;
  flügelBox: Box2;
}

function findBestMatchingElementMeta({
  elementMetas,
  size,
  füllungAuswahlwert,
  showProfil,
  profilAuswahlwert,
  profilleisteAuswahlwert,
  rahmenAuswahlwert,
  füllungsseiteAuswahlwert,
  anschlagAuswahlwert,
  öffnungAuswahlwert,
  eAntriebAuswahlwert,
  massAuswahlwert,
}: {
  elementMetas: (MElement | MTor)[];
  size: Vector2;
  füllungAuswahlwert: MAuswahlwert;
  showProfil: boolean;
  profilAuswahlwert: MAuswahlwert;
  profilleisteAuswahlwert: MAuswahlwert;
  rahmenAuswahlwert: MAuswahlwert;
  füllungsseiteAuswahlwert: MAuswahlwert;
  anschlagAuswahlwert: MAuswahlwert;
  öffnungAuswahlwert: MAuswahlwert;
  eAntriebAuswahlwert: MAuswahlwert;
  massAuswahlwert: MAuswahlwert;
}): MElement | MTor {
  const matchesAuswahlwert =
    <M0, P0 extends PProxy<M0>>(MAGIC: string, value: M0, path: PathFn<P0, M0>) =>
    (m: M0) =>
      value == null ||
      (filterPathIntersectionCached(MAGIC)(m, path).some((v) => v === value) ?? false);

  const matchesFüllung = matchesAuswahlwert(
    "elementFunctions.ts:210",
    füllungAuswahlwert,
    (e: PAuswahlwert) => e.Füllung_Objekt.Auswahlwert
  );
  const matchesProfil = (elementMeta: MTor) =>
    showProfil
      ? // filterPathIntersection liefert hier falsche Ergebnisse
        // Obwohl keine passende Profilkategorie und damit auch kein passendes Profil
        // vorhanden ist, gibt filterPathIntersection teilweise gültige matches zurück
        elementMeta.Profilkategorie?.map((p) => p.Profile).some((p) =>
          p?.map((profil) => profil.Auswahlwert).includes(profilAuswahlwert)
        )
      : () => true;
  const matchesProfilkategorie = (elementMeta: MTor) =>
    showProfil
      ? Commons.isEmpty(elementMeta.Profilkategorie)
        ? false
        : matchesProfil(elementMeta)
      : () => true;
  const matchesProfilleiste = showProfil
    ? matchesAuswahlwert(
        "elementFunctions.ts:216",
        profilleisteAuswahlwert,
        (e: PAuswahlwert) => e.Profilkategorie.Profilleiste.Auswahlwert
      )
    : () => true;
  const matchesRahmen = (element: MElement | MTor) =>
    rahmenAuswahlwert == null ||
    (filterPathIntersectionCached("elementFunctions.ts:218")(
      element,
      (e: PElement) => e.Rahmen.Auswahlwert
    ).some((v) => v === rahmenAuswahlwert) ??
      false);
  const matchesFüllungsseite = matchesAuswahlwert(
    "elementFunctions.ts:219",
    füllungsseiteAuswahlwert,
    (e: PAuswahlwert) => e.Füllungsseite_Objekt.Auswahlwert
  );
  const matchesAnschlag = matchesAuswahlwert(
    "elementFunctions.ts:223",
    anschlagAuswahlwert,
    (e: PAuswahlwert) => e.Anschlag_Objekt.Auswahlwert
  );
  const matchesÖffnung = matchesAuswahlwert(
    "elementFunctions.ts:224",
    öffnungAuswahlwert,
    (e: PAuswahlwert) => e.Öffnung_Objekt.Auswahlwert
  );
  const matchesMass = matchesAuswahlwert(
    "elementFunctions.ts:252",
    massAuswahlwert,
    (e: PAuswahlwert) => e.Mass.EasyAuswahlwert
  );
  const matchesEAntrieb = (e) =>
    e.E_Antrieb_Vorhanden == (eAntriebAuswahlwert?.Key == "E_Antrieb_mit");
  const matchesStandardtor = (e) => !e.Masstor && !e.Sondertor;
  const matchesTorKombination = (e) =>
    e.DIN_Richtung?.Tor_Kombinationen.some(
      ({ Anschlag, Füllungsseite, Öffnung }) =>
        anschlagAuswahlwert == Anschlag.Auswahlwert &&
        füllungsseiteAuswahlwert == Füllungsseite.Auswahlwert &&
        öffnungAuswahlwert == Öffnung.Auswahlwert
    );
  const matchesStandardMassUndVariabelFix = (e) => {
    return sizeScore(e, size, 0) === 0 && e.Mass.Variabel == "fix";
  };
  const matchesNurMitSpezialprofil = (e) => (e.nurMitSpezialprofil ? showProfil : true);

  const elementScore = (element) => {
    const debug = false;
    if (debug) {
      console.log("***********************");
      console.log("Scoring for element ", element.Key);
      console.log(" sizeScore(element, size, 0): ", sizeScore(element, size, 0));
      console.log(
        " matchesStandardMassUndVariabelFix: ",
        matchesStandardMassUndVariabelFix(element) ? 0 : -0.08
      );
      console.log(" matchesFüllung: ", matchesFüllung(element) ? 0 : -0.05);
      console.log(" matchesProfil: ", matchesProfil(element) ? 0 : -0.16);
      console.log(" matchesProfilkategorie: ", matchesProfilkategorie(element) ? 0 : -0.2);
      console.log(" matchesProfilleiste: ", matchesProfilleiste(element) ? 0 : -0.05);
      console.log(" matchesRahmen: ", matchesRahmen(element) ? 0 : -0.07);
      console.log(" matchesFüllungsseite: ", matchesFüllungsseite(element) ? 0 : -0.05);
      console.log(" matchesAnschlag: ", matchesAnschlag(element) ? 0 : -0.05);
      console.log(" matchesÖffnung: ", matchesÖffnung(element) ? 0 : -0.02);
      console.log(" matchesEAntrieb: ", matchesEAntrieb(element) ? 0 : -0.14);
      console.log(" matchesStandardtor: ", matchesStandardtor(element) ? 0 : -0.01);
      console.log(" matchesTorKombination: ", matchesTorKombination(element) ? 0 : -0.06);
      console.log(" matchesMass: ", matchesMass(element) ? 0 : -0.04);
      console.log(" matchesNurMitSpezialprofil: ", matchesNurMitSpezialprofil(element) ? 0 : -0.01);
    }

    const score =
      sizeScore(element, size, 0) +
      // Die Longlife Serie hat sowohl ein fixes Element mit Rahmen und 180x180cm Abmessung, als auch ein variables (Variabel:"fertigung") Element
      // mit den gleichen Standardmaßen. Das Element mit Variabel "fix" soll bei gewählter Standardhöhe und Breite bevorzugt werden.
      // Desshalb muss hier ein Score erzielt werden, der unter dem geimeinsamen Score von matchesRahmen und matchesMass liegt.
      (matchesStandardMassUndVariabelFix(element) ? 0 : -0.08) +
      // Elemente gleicher Form und gleicher Serie sind austauschbar, und es muss das Element
      // gefunden werden, welches die Auswahlwerte am besten wiedergibt.
      // Das ist z.B. wichtig bei abweichendem Torrahmen oder Dekoprofil vom StandardTor - in diesem
      // Fall soll automatisch ein Maßtor verwendet werden
      (matchesFüllung(element) ? 0 : -0.05) +
      // Das Einfügen eines Dekorprofil soll einen Wechsel von Standard auf Sondertor oder Masstor bewirken. Desshalb muss
      (matchesProfil(element) ? 0 : -0.16) +
      (matchesProfilkategorie(element) ? 0 : -0.2) +
      (matchesProfilleiste(element) ? 0 : -0.05) +
      (matchesRahmen(element) ? 0 : -0.08) +
      (matchesFüllungsseite(element) ? 0 : -0.05) +
      (matchesAnschlag(element) ? 0 : -0.05) +
      (matchesÖffnung(element) ? 0 : -0.02) +
      (matchesEAntrieb(element) ? 0 : -0.14) +
      // Masstore werden benachteiligt, damit Standardtore bevorzugt werden
      (matchesStandardtor(element) ? 0 : -0.01) +
      // Muss höher gewichtet werden als matchesAnschlag
      (matchesTorKombination(element) ? 0 : -0.06) +
      // Sollte höher gewichtet werden als matchesRahmen
      (matchesMass(element) ? 0 : -0.04) +
      // Elemente die nur in Kombination mit Spezialprofilen möglich sind sollen
      // niedriger gewichtet werden wenn kein Spezialprofil gesetzt ist
      (matchesNurMitSpezialprofil(element) ? 0 : -0.01);

    if (debug) {
      console.log("Final Score: ", score);
      console.log("***********************");
    }
    return score;
  };
  const winnerMeta = getHighestScoredMeta(elementMetas, elementScore);
  return winnerMeta;
}

export interface ElementMeta {
  serie: MSerie;
  form: MObjekt;
  füllungAuswahlwert: MAuswahlwert;
  rahmenAuswahlwert: MAuswahlwert;
  öffnungAuswahlwert: MAuswahlwert;
  anschlagAuswahlwert: MAuswahlwert;
  schieberichtungAuswahlwert: MAuswahlwert;
  schiebetormotorseiteAuswahlwert: MAuswahlwert;
  füllungsseiteAuswahlwert: MAuswahlwert;
  abschlussleisteAuswahlwert: MAuswahlwert;
  profilKategorieAuswahlwert: MAuswahlwert;
  profilAuswahlwert: MAuswahlwert;
  profilleisteAuswahlwert: MAuswahlwert;
  eAntriebAuswahlwert: MAuswahlwert;
  drückerAuswahlwert: MAuswahlwert;
  profilzylinderAuswahlwert: MAuswahlwert;
  aufsatzleisteAuswahlwert: MAuswahlwert;
  massAuswahlwert: MAuswahlwert;
  toröffnerAuswahlwert: MAuswahlwert;
  torschließerAuswahlwert: MAuswahlwert;
  isSchiebetor: boolean;
  isDoppeltor: boolean;
  isGehflügelLinks: boolean;
  standflügelbreite: number;
  gehflügelbreite: number;
  size: Vector3;
  showProfil: boolean;
  torMaxBreite: number;
  isGehflügelAufhängungAnMauer: boolean;
  isAnschlagprofil: boolean;
  höhe: number;
  breite: number;
  isAufhängungAnLinkerTorseite: boolean;
}

export interface ElementMetaParameters {
  massMetas: MMass[];
  sizeRange: SizeRange;
  isBreiteKonfigurierbar: boolean;
  meta: MElement;
  positionY: number;
  füllungMeta: MObjekt;
  füllungAuswahlwert: MAuswahlwert;
  profilKategorieAuswahlwert: MAuswahlwert;
  profilMeta: MObjekt;
  showProfil: boolean;
  profilAuswahlwert: MAuswahlwert;
  profilleisteAuswahlwert: MAuswahlwert;
  rahmenMeta: MObjekt;
  rahmenAuswahlwert: MAuswahlwert;
  abschlussleisteAuswahlwert: MAuswahlwert;
  eAntriebAuswahlwert: MAuswahlwert;
  öffnungAuswahlwert: MAuswahlwert;
  drückerAuswahlwert: MAuswahlwert;
  profilzylinderAuswahlwert: MAuswahlwert;
  aufsatzleisteAuswahlwert: MAuswahlwert;
  massAuswahlwert: MAuswahlwert;
  toröffnerAuswahlwert: MAuswahlwert;
  torschließerAuswahlwert: MAuswahlwert;
  verringerterBodenabstand: number;
  schiebetorRahmenDef: RahmenDef;
  seiteGewechselt?: boolean;
}

export function getElementMetaParameter({
  serie,
  form,
  füllungAuswahlwert,
  rahmenAuswahlwert,
  öffnungAuswahlwert,
  anschlagAuswahlwert,
  schieberichtungAuswahlwert,
  schiebetormotorseiteAuswahlwert,
  füllungsseiteAuswahlwert,
  abschlussleisteAuswahlwert,
  profilKategorieAuswahlwert,
  profilAuswahlwert,
  profilleisteAuswahlwert,
  eAntriebAuswahlwert,
  profilzylinderAuswahlwert,
  drückerAuswahlwert,
  isSchiebetor,
  isDoppeltor,
  isGehflügelLinks,
  standflügelbreite,
  gehflügelbreite,
  size,
  showProfil,
  torMaxBreite,
  aufsatzleisteAuswahlwert,
  massAuswahlwert,
  toröffnerAuswahlwert,
  torschließerAuswahlwert,
  isGehflügelAufhängungAnMauer,
  isAnschlagprofil,
  höhe,
  breite,
  isAufhängungAnLinkerTorseite,
}: ElementMeta): ElementMetaParameters {
  let flügelSizes: Vector2[] = undefined;
  if (!isSchiebetor) {
    flügelSizes = getFlügelSizesV2({
      isDoppeltor,
      isGehflügelLinks,
      standflügelbreite,
      gehflügelbreite,
      size: size as any as Vector2,
    });
  }

  const { elementMetasMassFüllung, elementMetasMass, elementMetasFormFüllung, elementMetasForm } =
    getElementMetaCombinations({
      form,
      // nicht benötigt für Schiebetore
      flügelSizes,
      füllungAuswahlwert,
    });

  const elementMetas =
    [elementMetasMassFüllung, elementMetasMass, elementMetasFormFüllung, elementMetasForm].find(
      (a) => a.length > 0
    ) ?? [];

  const massMetas = filterPathIntersectionCached("elementFunctions.ts:337")(
    elementMetas,
    (p: PTor) => p.Mass
  ) as MMass[];

  if (massMetas.length === 0) debugger;

  const { flügelBox, box, Schrittweite, isBreiteKonfigurierbar } = getSizeRange(
    massMetas,
    torMaxBreite
  );
  const sizeRange = { box, flügelBox, Schrittweite };

  // Distance: hier geht es um den Sore, den die Flügel-Sizes erhalten.
  // Der Distance-Score des Elementes ist min(flügel-score)

  const meta = findBestMatchingElementMeta({
    elementMetas,
    size: size as any as Vector2,
    füllungAuswahlwert,
    showProfil,
    profilAuswahlwert,
    profilleisteAuswahlwert,
    rahmenAuswahlwert,
    füllungsseiteAuswahlwert,
    anschlagAuswahlwert,
    öffnungAuswahlwert,
    eAntriebAuswahlwert,
    massAuswahlwert,
  });

  let schiebetorRahmenDef: RahmenDef = null;
  if (isSchiebetor) {
    schiebetorRahmenDef = erzeugeSchiebetorRahmen({
      torHöhe: höhe,
      lichteWeite: breite,
      schiebetorFlügelMaxBreite: meta.Mass.SchiebetorFlügelMaxBreite,
      gespiegeltZ: schieberichtungAuswahlwert.Key === "Schieberichtung_rechts",
    } as any);
    flügelSizes = schiebetorRahmenDef.füllungsFenster.map((b) => b.getSize(v2()));
  }

  // console.log(
  //   "findBestMatchingElementMeta",
  //   {
  //     elementMetas,
  //     size,
  //     flügelSizes,
  //     füllungAuswahlwert,
  //     showProfil,
  //     profilAuswahlwert,
  //     profilleisteAuswahlwert,
  //     rahmenAuswahlwert,
  //     füllungsseiteAuswahlwert,
  //     anschlagAuswahlwert,
  //     öffnungAuswahlwert,
  //   },
  //   " RESULT: ",
  //   meta
  // );

  if (meta == null) debugger;

  const füllungMeta = findPathIntersectionCached("elementFunctions.ts:417")(
    meta,
    (p: PTor) => p.Füllung,
    following_are_optionals,
    füllungAuswahlwert,
    (p: PAuswahlwert) => p.Füllung_Objekt,
    APP.arrangement.füllungAuswahlwert,
    (p: PAuswahlwert) => p.Füllung_Objekt
  ) as MObjekt;
  füllungAuswahlwert = füllungMeta?.Auswahlwert;

  profilKategorieAuswahlwert = getProfilKategorieAuswahlwert({
    meta,
    profilAuswahlwert,
    profilKategorieAuswahlwert,
  });

  const profilMetas = getProfilMetas({ profilAuswahlwert, profilKategorieAuswahlwert });

  const profilMeta: MObjekt = Commons.isEmpty(profilMetas)
    ? null
    : profilAuswahlwert == undefined
      ? profilMetas.find((p) => !isFüllungsModelEqual(p.Auswahlwert, füllungAuswahlwert)) // Initiales Einfügen eines Dekorprofil
      : profilMetas[0];
  profilAuswahlwert = profilMeta?.Auswahlwert;

  if (profilAuswahlwert === undefined) {
    showProfil = false;
  }

  // Dekorprofil soll nur gesetzt werden, wenn sich die Füllung des Dekorprofil
  // von den restlichen Profilen unterscheidet
  if (isFüllungsModelEqual(profilAuswahlwert, füllungAuswahlwert)) {
    profilAuswahlwert = null;
    profilKategorieAuswahlwert = null;
    showProfil = false;
  }

  profilleisteAuswahlwert = findPathIntersectionCached("elementFunctions.ts:463")(
    meta,
    (p: PElement) => p.Profilkategorie.Profilleiste.Auswahlwert,
    following_are_optionals,
    profilleisteAuswahlwert,
    (p: PAuswahlwert) => p.Profilleiste_Objekt.Auswahlwert,
    APP.arrangement.profilleisteAuswahlwert,
    (p: PAuswahlwert) => p
  ) as MAuswahlwert;

  const rahmenMeta = findPathIntersectionCached("elementFunctions.ts:471")(
    meta,
    (p: PElement) => p.Rahmen,
    following_are_optionals,
    rahmenAuswahlwert,
    (p: PAuswahlwert) => p.Rahmen_Objekt
  ) as MObjekt;

  rahmenAuswahlwert = rahmenMeta?.Auswahlwert;

  const abschlussleisteAuswahlwerte = getAbschlussleistenAuswahlwerteForElemente([{ meta, size }]);

  abschlussleisteAuswahlwert = APP.richMetadata.ensureAuswahlWert(
    abschlussleisteAuswahlwerte,
    abschlussleisteAuswahlwert
  );

  eAntriebAuswahlwert = findEAntriebAuswahlwert(meta, flügelSizes, eAntriebAuswahlwert);

  if ("Profilzylinder" in meta) {
    profilzylinderAuswahlwert = APP.richMetadata.ensureAuswahlWert(
      meta.Profilzylinder?.map((x) => x.Auswahlwert) ?? [],
      profilzylinderAuswahlwert
    );
    drückerAuswahlwert = APP.richMetadata.ensureAuswahlWert(
      meta.Drücker?.map((x) => x.Auswahlwert) ?? [],
      drückerAuswahlwert
    );
    öffnungAuswahlwert = APP.richMetadata.ensureAuswahlWert(
      meta.Öffnung?.map((x) => x.Auswahlwert) ?? [],
      öffnungAuswahlwert
    );
  }

  const aufsatzleisteMeta = findPathIntersectionCached("elementFunctions.ts:503")(
    meta,
    (p: PElement) => p.Aufsatzleiste,
    following_are_optionals,
    aufsatzleisteAuswahlwert,
    (p: PAuswahlwert) => p.Aufsatzleiste_Objekt,
    APP.arrangement.aufsatzleisteAuswahlwert,
    (p: PAuswahlwert) => p.Aufsatzleiste_Objekt
  ) as MObjekt;

  aufsatzleisteAuswahlwert = aufsatzleisteMeta?.Auswahlwert;

  if ("Toröffner" in meta) {
    toröffnerAuswahlwert = APP.richMetadata.ensureAuswahlWert(
      isToröffnerMöglichFn(meta, isAnschlagprofil)
        ? meta.Toröffner?.map((x) => x.Auswahlwert) ?? []
        : [],
      toröffnerAuswahlwert
    );
  }

  if ("Torschließer" in meta) {
    torschließerAuswahlwert = APP.richMetadata.ensureAuswahlWert(
      isTorschließerMöglichFn(
        meta,
        isDoppeltorFn(meta) ? gehflügelbreite : flügelSizes[0].x,
        isGehflügelAufhängungAnMauer
      )
        ? meta.Torschließer?.map((x) => x.Auswahlwert) ?? []
        : [],
      torschließerAuswahlwert
    );
  }

  let positionY = positionYOf(meta);
  const bodenabstandVerringern = profilMeta?.Profilkategorie?.find((k) => k.BodenabstandVerringern);
  let verringerterBodenabstand = bodenabstandVerringern
    ? // nicht ganz klar, warum * 0.5 => aber so passt die Höhe mit den Elementen ohne Lichtleiste
      getProfilStapelHöhe(profilMeta).stapelhöhe * 0.5
    : 0;
  positionY -= verringerterBodenabstand;

  return {
    massMetas,
    sizeRange,
    isBreiteKonfigurierbar,
    meta,
    positionY,
    füllungMeta,
    füllungAuswahlwert,
    profilKategorieAuswahlwert,
    profilMeta,
    showProfil,
    profilAuswahlwert,
    profilleisteAuswahlwert,
    eAntriebAuswahlwert,
    rahmenMeta,
    rahmenAuswahlwert,
    abschlussleisteAuswahlwert,
    profilzylinderAuswahlwert,
    drückerAuswahlwert,
    öffnungAuswahlwert,
    aufsatzleisteAuswahlwert,
    massAuswahlwert,
    toröffnerAuswahlwert,
    torschließerAuswahlwert,
    verringerterBodenabstand,
    schiebetorRahmenDef,
  };
}

/**
 * Dedupliziert Profilkategorien, die in der UI denselben Eintrag bilden würden
 * (gleiche Profile bzw. Teilmenge davon und gleiche ProfilAnzahl). Von zwei
 * solchen Kategorien bleibt nur eine übrig; Nicht-Mass-Kategorien (z.B. Zaunfeld)
 * werden gegenüber Mass-Varianten (z.B. Mass-Tore, Schiebetore) bevorzugt.
 *
 * Wichtig: Wird die Mass-Variante j ausgeschieden, wird NICHT abgebrochen –
 * die aktuelle Kategorie i kann zusätzlich Teilmenge einer Nicht-Mass-Kategorie
 * sein, die i dann ebenfalls ausscheidet. Andernfalls überlebten z.B. eine
 * Schiebetor- und eine Zaunfeld-Kategorie mit gleichem Namen nebeneinander
 * (abhängig von der Reihenfolge der Kandidaten).
 */
export function filterByUniqueProfiles(profilkategorien: MProfilkategorie[]) {
  profilkategorien = profilkategorien.slice().reverse();
  const n = profilkategorien.length;
  const isDuplicate = new Array(n).fill(false);

  function isSubset(aProfile: MObjekt[], bProfile: MObjekt[]) {
    const bIds = new Set(bProfile.map((p) => p.id));
    return aProfile.every((p) => bIds.has(p.id));
  }

  function isEqualProfilAnzahl(
    profilkategorieA: MProfilkategorie,
    profilkategorieB: MProfilkategorie
  ) {
    return profilkategorieA.ProfilAnzahl === profilkategorieB.ProfilAnzahl;
  }

  function isMassProfilkategorie(profilkategorie: MProfilkategorie) {
    return profilkategorie.Element.every((e: MElement | MTor) => (e as MTor).Masstor);
  }

  for (let i = 0; i < n; i++) {
    if (isDuplicate[i]) continue;
    const aProfiles = profilkategorien[i].Profile;

    for (let j = 0; j < n; j++) {
      if (j === i || isDuplicate[j]) continue;
      const bProfiles = profilkategorien[j].Profile;

      if (
        isSubset(aProfiles, bProfiles) &&
        isEqualProfilAnzahl(profilkategorien[i], profilkategorien[j])
      ) {
        // Profilkategorien für Sondertore bevorzugen
        if (isMassProfilkategorie(profilkategorien[j])) {
          isDuplicate[j] = true;
        } else {
          isDuplicate[i] = true;
          break;
        }
      }
    }
  }

  return profilkategorien.filter((_, idx) => !isDuplicate[idx]);
}

export function getRichProfilkategorien(includeKeinProfilOption = false): ProfilkategorieData[] {
  const agt = APP.arrangement;
  const { Profilkategorie } = agt.auswahlAccessoren;

  const filterErlaubteAuswahlwerte = (auswahlwerte) =>
    auswahlwerte.filter((auswahlwert) => APP.arrangement.füllungAuswahlwert !== auswahlwert);

  const elementeForProfiles = APP.arrangement.getSelectedOrAllElemente();
  const katVonElementen = elementeForProfiles.flatMap((e) => e.meta?.Profilkategorie ?? []);
  const katVonOptions =
    (Profilkategorie?.möglicheAuswahlwerte
      ?.map((p) => _.first(p.Profilkategorie))
      .filter(Boolean) as MProfilkategorie[]) ?? [];

  const möglicheProfilkategorien: MProfilkategorie[] = uniq(
    sortForSortierung([...katVonOptions, ...katVonElementen] as any) as MProfilkategorie[]
  );

  const getProfileFromProfilkategorie = (kategorieAuswahlwert) =>
    filterErlaubteAuswahlwerte(
      filterPathIntersectionCached("DekorOptionen.jsx:100")(
        kategorieAuswahlwert,
        (a: PAuswahlwert) => a.Profilkategorie.Profile.Auswahlwert
      )
    );

  const getProfilleistenFromProfilkategorie = (kategorieAuswahlwert: MAuswahlwert) =>
    filterPathIntersectionCached("DekorOptionen.jsx:107")(
      kategorieAuswahlwert,
      (a: PAuswahlwert) => a.Profilkategorie.Profilleiste.Auswahlwert
    ) as MAuswahlwert[];

  const profilkategorien =
    möglicheProfilkategorien
      ?.map((profilkategorie) => {
        const kategorieAuswahlwert = profilkategorie.Auswahlwert;
        const profilAuswahlwerte = getProfileFromProfilkategorie(kategorieAuswahlwert);
        const profilleisteAuswahlwerte = getProfilleistenFromProfilkategorie(kategorieAuswahlwert);
        return {
          profilkategorie,
          kategorieAuswahlwert,
          profilAuswahlwerte,
          profilleisteAuswahlwerte,
        };
      })
      .filter(
        ({ profilAuswahlwerte, profilleisteAuswahlwerte }) =>
          !isEmpty(profilAuswahlwerte) || !isEmpty(profilleisteAuswahlwerte)
      ) ?? [];

  // Zwei MProfilkategorien können denselben Auswahlwert teilen (z.B. eine Variante
  // für Sondertore und eine Variante aus einem Element-Backref). Die Dedup muss
  // daher über die Profilkategorie-Referenzen laufen, nicht über den Auswahlwert,
  // sonst blieben beide Einträge mit demselben Auswahlwert übrig.
  const filteredProfilkategorien = filterByUniqueProfiles(
    profilkategorien.map(({ profilkategorie }) => profilkategorie)
  );
  const uniqProfilkategorien: ProfilkategorieData[] = profilkategorien
    .filter(({ profilkategorie }) => filteredProfilkategorien.includes(profilkategorie))
    .map(({ kategorieAuswahlwert, profilAuswahlwerte, profilleisteAuswahlwerte }) => ({
      kategorieAuswahlwert,
      profilAuswahlwerte,
      profilleisteAuswahlwerte,
    }));

  if (includeKeinProfilOption)
    uniqProfilkategorien.push({
      kategorieAuswahlwert: APP.richMetadata.findAuswahlwert("KEIN_DEKORPROFIL"),
      profilAuswahlwerte: [],
      profilleisteAuswahlwerte: [],
    });

  return uniqProfilkategorien;
}

export const applyProfileChangeToElements = (
  elemente: any[],
  kategorieAuswahlwert: any,
  updates: { profil?: any; profilleiste?: any },
  individualisiert = false
) => {
  const agt = APP.arrangement;
  const alleElemente = agt?.getElemente?.() ?? elemente;

  // Globale Profilposition übernehmen, wenn keine Individualisierung vorliegt
  const globaleProfilPosition = agt?.profilPosition_ ?? null;

  elemente.forEach((e) => {
    // Check if this element's meta actively supports this category
    const isValid =
      (e.isEinzeltor && e.isStandardMass) ||
      e.meta?.Profilkategorie?.some(
        (pk: any) =>
          pk.Auswahlwert === kategorieAuswahlwert || pk.Auswahlwert?.id === kategorieAuswahlwert?.id
      );

    if (isValid) {
      // Bei globalem Setzen: individualisierte Elemente überspringen
      if (
        !individualisiert &&
        e.profilAuswahlwertIndividualisiert &&
        !agt?.isAllPartsIndividualisiert?.(alleElemente, "profilAuswahlwertIndividualisiert")
      ) {
        return;
      }
      const hatteVorherKeinProfil = !e.showProfil;
      if (hatteVorherKeinProfil) {
        e.setShowProfil ? e.setShowProfil(true) : (e.showProfil = true);
      }
      e.profilKategorieAuswahlwert = kategorieAuswahlwert;
      if (updates.profil !== undefined) e.profilAuswahlwert = updates.profil;
      if (updates.profilleiste !== undefined) e.profilleisteAuswahlwert = updates.profilleiste;
      // Globale Profilposition auf Elemente übertragen, die neu ein Profil bekommen
      if (hatteVorherKeinProfil && globaleProfilPosition != null) {
        e.profilPosition = globaleProfilPosition;
      }
      if (individualisiert) {
        e.profilAuswahlwertIndividualisiert = true;
        e.showIndividualisiert = true;
      }
    }
  });
  if (APP) APP.dirty();
};

export type ProfilkategorieData = {
  kategorieAuswahlwert: MAuswahlwert;
  profilAuswahlwerte: MAuswahlwert[];
  profilleisteAuswahlwerte: MAuswahlwert[];
};

export function findEAntriebAuswahlwert(
  meta: MElement,
  flügelSizes: Vector2[],
  eAntriebAuswahlwert: MAuswahlwert
) {
  return isDoppeltorFn(meta)
    ? isBreiteFlügelFürEAntrieb(flügelSizes) && isEantriebAuswahlMöglich(meta)
      ? eAntriebAuswahlwert ??
        APP.richMetadata.getAuswahlWertVoreinstellungForKey("E_Antrieb_Vorhanden")
      : APP.richMetadata.findAuswahlwert("E_Antrieb_ohne")
    : null;
}

export function getProfilKategorieAuswahlwert({
  meta,
  profilAuswahlwert,
  profilKategorieAuswahlwert,
}: {
  meta: MElement | MTor;
  profilAuswahlwert: MAuswahlwert;
  profilKategorieAuswahlwert: MAuswahlwert;
}) {
  const möglicheAuswahlwerte = filterPathIntersectionCached("elementFunctions.ts:443")(
    meta,
    (p: PElement) => p.Profilkategorie.Auswahlwert,
    following_are_optionals,
    profilAuswahlwert,
    (p: PAuswahlwert) => p.Objekt.Profilkategorie.Auswahlwert,
    profilKategorieAuswahlwert,
    (p: PAuswahlwert) => p
  );
  const defaultProfilkategorie = meta?.DefaultProfilkategorie?.Auswahlwert;
  if (möglicheAuswahlwerte.length == 0 || möglicheAuswahlwerte.includes(defaultProfilkategorie)) {
    // Initial soll die default Profilkategorie verwendet werden
    return defaultProfilkategorie;
  }
  return möglicheAuswahlwerte[0];
}

export function getProfilMetas({
  profilAuswahlwert,
  profilKategorieAuswahlwert,
}: {
  profilAuswahlwert: MAuswahlwert;
  profilKategorieAuswahlwert: MAuswahlwert;
}): MObjekt[] {
  const profilMetas = filterPathIntersectionCached("elementFunctions.ts:666")(
    profilKategorieAuswahlwert,
    (p: PAuswahlwert) => p.Profilkategorie.Profile,
    following_are_optionals,
    profilAuswahlwert,
    (p: PAuswahlwert) => p.Füllung_Objekt
  ) as MObjekt[];
  return profilMetas;
}

export function isFüllungsModelEqual(a, b) {
  const getMMR = (auswahlwert) => auswahlwert?.Objekt?.[0].ModelMaterialRelation?.[0];
  const ammr = getMMR(a);
  const bmmr = getMMR(b);
  return ammr?.Model == bmmr?.Model && _.isEqual(ammr?.MaterialMap, bmmr?.MaterialMap);
}

export function getFlügelSizesV2({
  isDoppeltor,
  isGehflügelLinks,
  standflügelbreite,
  gehflügelbreite,
  size,
}: {
  isDoppeltor: boolean;
  isGehflügelLinks: boolean;
  standflügelbreite: number;
  gehflügelbreite: number;
  size: Vector2;
}): Vector2[] {
  return isDoppeltor
    ? (isGehflügelLinks
        ? [standflügelbreite, gehflügelbreite]
        : [gehflügelbreite, standflügelbreite]
      ).map((x) => size.clone().setX(x))
    : [size];
}

export function getGehflügel({
  isDoppeltor,
  isGehflügelLinks,
  flügel,
}: {
  isDoppeltor: boolean;
  isGehflügelLinks: boolean;
  flügel: Flügel[];
}) {
  if (isDoppeltor) return isGehflügelLinks ? flügel[1] : flügel[0];
  return null;
}

export function getStandflügel({
  isDoppeltor,
  isGehflügelLinks,
  flügel,
}: {
  isDoppeltor: boolean;
  isGehflügelLinks: boolean;
  flügel: Flügel[];
}) {
  if (isDoppeltor) return isGehflügelLinks ? flügel[0] : flügel[1];
  return null;
}

export function positionYOf(meta: MElement) {
  return meta?.Mass?.PositionY ?? 0;
}

export interface UpdateSizeParamters {
  form: MObjekt;
  füllungAuswahlwert: MAuswahlwert;
  size: Vector3;
  sizeRange: SizeRange;
  isBreiteKonfigurierbar: boolean;
  isDoppeltor: boolean;
  isGehflügelLinks: boolean;
  standflügelbreite: number;
  gehflügelbreite: number;
  breite: number;
  höhe: number;
  massMetas: MMass[];
  massAuswahlwert: MAuswahlwert;
}

export function updateSize({
  form,
  füllungAuswahlwert,
  size,
  sizeRange,
  isBreiteKonfigurierbar,
  isDoppeltor,
  isGehflügelLinks,
  standflügelbreite,
  gehflügelbreite,
  breite,
  höhe,
  massMetas,
  massAuswahlwert,
}: UpdateSizeParamters) {
  const nullEpsilon = 0.01;
  const { flügelBox, Schrittweite } = sizeRange;
  if (isBreiteKonfigurierbar) {
    if (isDoppeltor) {
      if (standflügelbreite < nullEpsilon || gehflügelbreite < nullEpsilon) {
        gehflügelbreite = standflügelbreite = size.x;
      }
      breite = size.x = getBreiteFromFlügelBreiten({
        isDoppeltor,
        gehflügelbreite,
        standflügelbreite,
        breite,
      });
    } else {
      if (breite > nullEpsilon) {
        size.x = breite;
      }
    }
  }

  getFlügelSizesV2({
    isDoppeltor,
    isGehflügelLinks,
    standflügelbreite,
    gehflügelbreite,
    size: size as any as Vector2,
  }).forEach((flügelSize: Vector2, index: number) => {
    ({ breite, standflügelbreite, gehflügelbreite } = setFlügelBreite({
      isDoppeltor,
      isGehflügelLinks,
      gehflügelbreite,
      standflügelbreite,
      breite: roundSchrittweite("x", flügelSize.x, flügelBox, Schrittweite),
      index,
    }));
  });

  size.y = roundSchrittweite("y", höhe, flügelBox, Schrittweite);

  getFlügelSizesV2({
    isDoppeltor,
    isGehflügelLinks,
    standflügelbreite,
    gehflügelbreite,
    size: size as any as Vector2,
  }).forEach((flügelSize, index) => {
    clampBox2Vector3(flügelBox, flügelSize as any);
    ({ breite, standflügelbreite, gehflügelbreite } = setFlügelBreite({
      isDoppeltor,
      isGehflügelLinks,
      gehflügelbreite,
      standflügelbreite,
      breite: flügelSize.x,
      index,
    }));
  });

  const flügelSizes = getFlügelSizesV2({
    isDoppeltor,
    isGehflügelLinks,
    standflügelbreite,
    gehflügelbreite,
    size: size as any as Vector2,
  });
  const standardMass = massMetas.find((mass) =>
    flügelSizes.every((size) => {
      const isNotFertigung = mass.Variabel != "fertigung";
      const standardEqualsSize = mass.Standard.distanceTo(size) < sizeExactEpsilon;
      return isNotFertigung && standardEqualsSize;
    })
  );
  const isStandardMass = standardMass != null;
  if (standardMass) {
    massAuswahlwert =
      massMetas.find((mass) => mass.EasyAuswahlwert === massAuswahlwert)?.EasyAuswahlwert ??
      standardMass.EasyAuswahlwert;
  } else {
    const { elementMetasForm } = getElementMetaCombinations({
      form,
      flügelSizes,
      füllungAuswahlwert,
    });
    const matchingMasses = [...massMetas]
      .concat(
        filterPathIntersectionCached("elementFunctions.ts:643")(
          elementMetasForm,
          (p: PTor) => p.Mass
        )
      )
      .filter((mass) =>
        flügelSizes.every((size) => minMaxBox2(mass).distanceToPoint(size) < sizeExactEpsilon)
      );
    const sorted = matchingMasses
      .map((mass) => ({
        mass,
        score: _.sumBy(flügelSizes, (size) => mass.Standard.distanceTo(size)),
      }))
      .sort((a, b) => a.score - b.score);
    const bestMass = _.first(sorted)?.mass;
    if (bestMass) {
      massAuswahlwert = bestMass.EasyAuswahlwert;
    } else {
      // console.error('kein Mass-Auswahlwert vorhanden für',{size: ''+size, Form: form?.Key, Füllung: füllungAuswahlwert?.Key});
      debugger;
    }
  }
  return {
    breite,
    size,
    isStandardMass,
    massAuswahlwert,
    gehflügelbreite,
    standflügelbreite,
  };
}

export function aussenbreiteZuFlügelbreite(
  istDoppeltor: boolean,
  aussenbreite: number,
  zwischenabstand = 0
): number {
  const breite = aussenbreite - zwischenabstand;
  return istDoppeltor ? (breite - APP.settings.doppeltorFlügelabstand) / 2 : breite;
}

export function flügelbreiteZuAussenbreite(
  istDoppeltor: boolean,
  flügelbreite: number,
  zwischenabstand = 0
): number {
  return (
    (istDoppeltor ? flügelbreite * 2 + APP.settings.doppeltorFlügelabstand : flügelbreite) +
    zwischenabstand
  );
}

export function getBreiteFromFlügelBreiten({
  isDoppeltor,
  gehflügelbreite,
  standflügelbreite,
  breite,
}: {
  isDoppeltor: boolean;
  gehflügelbreite: number;
  standflügelbreite: number;
  breite: number;
}): number {
  return isDoppeltor
    ? gehflügelbreite + standflügelbreite + APP.settings.doppeltorFlügelabstand
    : breite;
}

export function setFlügelBreite({
  isDoppeltor,
  isGehflügelLinks,
  gehflügelbreite,
  standflügelbreite,
  breite,
  index,
}: {
  isDoppeltor: boolean;
  isGehflügelLinks: boolean;
  gehflügelbreite: number;
  standflügelbreite: number;
  breite: number;
  index: number;
}): {
  breite: number;
  standflügelbreite: number;
  gehflügelbreite: number;
} {
  if (isDoppeltor) {
    if (index === (isGehflügelLinks ? 1 : 0)) {
      standflügelbreite = breite;
    } else {
      gehflügelbreite = breite;
    }
    breite = getBreiteFromFlügelBreiten({
      isDoppeltor,
      gehflügelbreite,
      standflügelbreite,
      breite,
    });
  }
  return { breite, standflügelbreite, gehflügelbreite };
}

export function isTorFn(elementMeta: MKeyTable): boolean {
  return elementMeta?.Key !== undefined && elementMeta?.["Öffnung"] !== undefined;
}

export function isElementFn(elementMeta: MKeyTable): boolean {
  return elementMeta?.Key !== undefined && elementMeta?.["Mass"] !== undefined;
}

export function isPfostenFn(elementMeta: MKeyTable): boolean {
  return elementMeta?.Key !== undefined && elementMeta?.["Pfostenmass"] !== undefined;
}

export function isGlas(elementMeta: MElement): boolean {
  return elementMeta.DetailSerie.some((ds) => ds.Key == "SYSTEM GLAS");
}

/**
 * Berechnet die mögliche Breite von Elementen mit gleichem Abstand, wobei die gesamtBreite
 * die Breite ohne Endpfosten ist (und auch keine Einschübe in die Endpfosten berücksichtigt werden).
 * @param {number} gesamtBreite - Die Gesamtbreite des Bereichs, in den die Elemente eingefügt werden sollen.
 * @param {number} elementAbstand - Der Abstand zwischen den Elementen.
 * @param {number} elementBreite - Die Breite jedes Elements.
 * @returns {Object} Ein Objekt mit den Eigenschaften:
 * - anzahlGanzeElemente: Die maximale Anzahl von Elementen, die in die gesamtBreite passen.
 * - restBreite: Die übrig gebliebene Lichte Weite nach dem Einsetzen der Elemente, z.B. für Leerräume
 * - massBreite: Die übrig gebliebene Elementbreite nach dem Einsetzen der Elemente, also für ein passendes Maßelement
 * - gesamtBreiteElemente: Die Gesamtbreite aller Elemente inklusiv Elementabständen.
 * - anzahlElementeRational: Die Anzahl der Elemente als rationale Zahl.
 */
export const calcElementAnzahl = (
  gesamtBreite: number,
  elementAbstand: number,
  elementBreite: number
): {
  anzahlGanzeElemente: number;
  restBreite: number;
  gesamtBreiteElemente: number;
  anzahlElementeRational: number;
} => {
  const anzahlElementeRational = (gesamtBreite + elementAbstand) / (elementBreite + elementAbstand);
  const anzahlGanzeElemente = Math.floor(anzahlElementeRational);
  const gesamtBreiteElemente =
    elementBreite * anzahlGanzeElemente + elementAbstand * Math.max(0, anzahlGanzeElemente - 1);
  const restBreite = gesamtBreite - gesamtBreiteElemente;
  return {
    anzahlGanzeElemente,
    restBreite,
    gesamtBreiteElemente,
    anzahlElementeRational,
  };
};

/**
 * Berechnet die mögliche Breiten von Masselementen bei Gleichverteilung und gleichem Abstand, wobei die gesamtbreite
 * die Breite ohne Endpfosten ist (und auch keine Einschübe in die Endpfosten berücksichtigt werden).
 * @param {number} sollBreite - Die Gesamtbreite des Bereichs, in den die Elemente eingefügt werden sollen.
 * @param {number} elementAbstand - Der Abstand zwischen den Elementen.
 * @param {number} standardBreite - Die StandardBreite / Wunschbreite eines Elementes.
 * @param {number} minBreite - Die minimale Breite eines Elementes.
 * @param {number} maxBreite - Die maximale Breite eines Elementes.
 * * @returns {Object} Ein Objekt mit den Eigenschaften:
 * - gesamtBreite: Die sich ergebene Gesamtbreite, gesamtBreite + restBreite = sollBreite.
 * - anzahlElemente: Die Anzahl der Elemente
 * - idealeBreite: Die Breite eines Elementes
 * - restBreite: Die übrig gebliebene Breite, sollte immer 0 sein.
 */
export const calcElementAnzahlGleichverteilt = (
  sollBreite: number,
  elementAbstand: number,
  standardBreite: number,
  minBreite: number,
  maxBreite: number
): {
  anzahlElemente: number;
  elementBreite: number;
  restBreite: number;
  gesamtBreite: number;
} => {
  const anzahl = (elementBreite) =>
    calcElementAnzahl(sollBreite, elementAbstand, elementBreite).anzahlElementeRational;
  const elementBreiteBeiAnzahl = (anzahl) => calcElementBreite(sollBreite, elementAbstand, anzahl);
  const minAnzahl = anzahl(maxBreite);
  const maxAnzahl = anzahl(minBreite);
  const möglicheElementAnzahlen = _.range(Math.ceil(minAnzahl), Math.floor(maxAnzahl) + 1);
  const möglicheBreiteBeiAnzahl = möglicheElementAnzahlen.map(elementBreiteBeiAnzahl);
  const idealeBreite = _.minBy(möglicheBreiteBeiAnzahl, (breite: number) =>
    Math.abs(standardBreite - breite)
  );
  const { anzahlGanzeElemente, restBreite } = calcElementAnzahl(
    sollBreite,
    elementAbstand,
    idealeBreite
  );
  const gesamtBreite =
    anzahlGanzeElemente * idealeBreite + elementAbstand * Math.max(0, anzahlGanzeElemente - 1);
  return {
    anzahlElemente: anzahlGanzeElemente,
    elementBreite: idealeBreite,
    restBreite,
    gesamtBreite,
  };
};

/**
 * Berechnet die mögliche Breiten von Elementen inklusiv Masselementen mit gleichem Abstand, wobei die gesamtBreite
 * die Breite ohne Endpfosten ist (und auch keine Einschübe in die Endpfosten berücksichtigt werden).
 * @param {number} gesamtBreite - Die Gesamtbreite des Bereichs, in den die Elemente eingefügt werden sollen.
 * @param {number} elementAbstand - Der Abstand zwischen den Elementen.
 * @param {number} standardBreite - Die StandardBreite eines Elementes.
 * @param {number} minBreite - Die minimale Breite eines Elementes.
 * @param {number} maxBreite - Die maximale Breite eines Elementes.
 * @param {number} multipleElementsAllowed - Ist mehr als ein Element erlaubt? Default true
 * * @returns {Object} Ein Objekt mit den Eigenschaften:
 * - gesamtBreite: Die sich ergebene Gesamtbreite.
 * - elementBreiten: Array mit Elementbreiten. Zuerst die StandardBreiten, am Ende die Maßelemente.
 */
export const calcElementAnzahlStandardMitMasselementen = (
  sollBreite: number,
  elementAbstand: number,
  standardBreite: number,
  minBreite: number,
  maxBreite: number,
  { multipleElementsAllowed = true } = {}
): {
  elementBreiten: number[];
  restBreite: number;
  gesamtBreite: number;
} => {
  const epsilon = 0.001;
  let elementBreite = standardBreite;
  minBreite = Math.max(APP.arrangement.getElementMinBreite(), minBreite);

  const masselementFitsPerfectly =
    elementBreite < maxBreite + epsilon &&
    sollBreite < maxBreite + epsilon &&
    elementBreite < sollBreite + epsilon;

  if (masselementFitsPerfectly) {
    elementBreite = maxBreite;
  }

  let { anzahlGanzeElemente, restBreite, gesamtBreiteElemente, anzahlElementeRational } =
    calcElementAnzahl(sollBreite, elementAbstand, elementBreite);

  if (!masselementFitsPerfectly && !multipleElementsAllowed) {
    anzahlGanzeElemente = Math.min(1, anzahlGanzeElemente);
  }
  anzahlGanzeElemente = Math.max(0, anzahlGanzeElemente);

  let elementBreiten = new Array(anzahlGanzeElemente).fill(elementBreite);

  if (restBreite > epsilon && (multipleElementsAllowed || anzahlGanzeElemente == 0)) {
    // weil mindestens ein weiteres Element hinzugefügt werden wird
    if (anzahlGanzeElemente > 0) {
      restBreite -= elementAbstand;
    }

    const clamp = (breite: number) => Maths.cutBetween(minBreite, breite, maxBreite);
    const massElementBedingung = () => restBreite + epsilon > minBreite;
    if ((restBreite < minBreite || restBreite > maxBreite) && anzahlGanzeElemente > 0) {
      // Falls die letzte Lücke nicht geschlossen werden kann, verteile den Leerraum auf zwei
      // möglichst gleichbreite Maßelemente. Mache dazu das letzte Standardelement zu einem Maßelement
      // und füge ein weitere hinzu
      const neueRestBreite = restBreite + elementBreite;
      const neueBreite = clamp(neueRestBreite / 2);
      if (Math.abs(neueBreite - elementBreite) > epsilon) {
        elementBreiten[elementBreiten.length - 1] = neueBreite;
        restBreite = neueRestBreite - neueBreite;
      }
    }
    if (massElementBedingung() || elementBreiten.length == 0) {
      const neueBreite = clamp(restBreite);
      elementBreiten.push(neueBreite);
      restBreite -= neueBreite;
    }
  }

  const gesamtBreite =
    _.sum(elementBreiten) + elementAbstand * Math.max(0, elementBreiten.length - 1);

  return {
    elementBreiten,
    gesamtBreite,
    restBreite,
  };
};

/**
 * berechnet die mögliche Breite von Elementen mit gleichem Abstand, wobei die gesamtBreite
 * die Breite ohne Endpfosten ist (und auch keine Einschübe in die Endpfosten berücksichtigt werden).
 * Achtung: Die Funktion ist nicht kontinuierlich, denn bei anzahl < 1 wird der elementAbstand
 * nicht berücksichtigt, bei anzahl > 1 wird der elementAbstand dagegen berücksichtigt
 */
export const calcElementBreite = (
  gesamtBreite: number,
  elementAbstand: number,
  anzahl: number
): number =>
  anzahl <= 1 + Number.EPSILON
    ? gesamtBreite / anzahl
    : (gesamtBreite + elementAbstand) / anzahl - elementAbstand;

export function getElementAnzahlDieInsEmptyPasst(
  element: any,
  empty: any,
  elementBreite = element.breite
): {
  anzahlGanzeElemente: number;
  restBreite: number;
  gesamtBreiteElemente: number;
  elementBreite: number;
  sollBreite: number;
  sollBreiteElemente: number;
  deltaLinks: number;
  deltaRechts: number;
  elementZwischenAbstand: number;
} {
  // die errechnete Anzahl bezieht sich immer auf einen freistehenden, linearen Zaunabschnitt
  const deltaLinks = getIstSollZwischenabstandDelta(empty, element, LINKS);
  const deltaRechts = getIstSollZwischenabstandDelta(empty, element, RECHTS);

  const sollBreite = empty.breite;

  const elementZwischenAbstand = getElementZuElementDeltasFürGleicheElemente(element);

  const sollBreiteElemente = sollBreite + deltaLinks + deltaRechts;
  const { anzahlGanzeElemente, restBreite, gesamtBreiteElemente } = calcElementAnzahl(
    sollBreiteElemente,
    elementZwischenAbstand,
    elementBreite
  );

  // console.log(
  //   "getElementAnzahlDieInsEmptyPasst",
  //   Debugs.roundValues({
  //     anzahl,
  //     elementBreite,
  //     deltaLinks,
  //     deltaRechts,
  //     sollBreite,
  //     sollElementAbstand,
  //     sollGap1,
  //     sollGap2,
  //     sollUmprofilBreite1,
  //     sollUmprofilBreite2,
  //     sollPfostenBreite,
  //   })
  // );

  return {
    anzahlGanzeElemente,
    restBreite,
    gesamtBreiteElemente,
    elementBreite,
    sollBreite,
    sollBreiteElemente,
    deltaLinks,
    deltaRechts,
    elementZwischenAbstand,
  };
}

export const öffnungswinkelItem = (part: any) =>
  [
    {
      angle: clampTorÖffnungswinkelFürMauer(part, part.öffnungswinkel.x),
      label: I18N("Tor leicht öffnen"),
    },
    {
      angle: clampTorÖffnungswinkelFürMauer(part, part.öffnungswinkel.y),
      label: I18N("Tor ganz öffnen"),
    },
    { angle: 0, label: I18N("Tor schließen") },
  ].filter((item, index, all) => all.findIndex((x) => x.angle === item.angle) === index);

export const getMaxÖffnungswinkelTorAnMauer = () => {
  const configured = Number(APP.settings?.["öffnungswinkelTorAnMauer"]);
  return Number.isFinite(configured) ? configured : 90;
};

export const clampTorÖffnungswinkelFürMauer = (part: any, winkel: number) =>
  part?.anMauer ? Math.min(winkel, getMaxÖffnungswinkelTorAnMauer()) : winkel;

export const öffnungswinkelItemOf = (
  part: any
): {
  angle: number;
  label: string;
} =>
  _.minBy(öffnungswinkelItem(part), (x) => Math.abs(x.angle - part.torÖffnungswinkel)) ??
  _.first(öffnungswinkelItem(part));

/**
 * Gibt die passendste Auswahl zurück
 * 1. Kriterium (muss): Auswahl.Key == AuswahlKey
 * 2. Kriterium: WorkflowKey ist in auswahl.Workflow enthalten (besser) oder auswahl.Workflow ist leer (okay)
 * 3. Kriterium: DetailSerien überschneidet sich mit auswahl.DetailSerie (besser) oder auswahl.DetailSerie ist leer (okay)
 * Sollte nach der Filterung nichts mehr übrig sein, so werden alle Auswahlen mit passendem Key zurückgegeben
 * @param AuswahlKey
 * @param DetailSerien
 * @param WorkflowKey
 */
export const findAuswahl = (
  AuswahlKey: string,
  DetailSerien: MDetailSerie[] = [],
  WorkflowKey?: string
) => {
  const auswahlenMitKey = APP.metadata.Auswahl.filter((auswahl) => auswahl.Key == AuswahlKey);
  const bestAuswahl = maxByIfGreaterZero(auswahlenMitKey, (auswahl: MAuswahl) => {
    let score = 1;
    if (!Commons.isEmpty(auswahl.Workflow))
      score += auswahl.Workflow.some((workflow) => workflow.Auswahlwert.Key === WorkflowKey)
        ? 2
        : -100;
    if (!Commons.isEmpty(auswahl.DetailSerie))
      score += auswahl.DetailSerie.some((detailSerie) => DetailSerien.includes(detailSerie))
        ? 1
        : -100;

    return score;
  });
  return bestAuswahl || _.first(auswahlenMitKey);
};

export function partToWorkflow(part_: any): {
  DetailSerien: MObjekt[];
  Workflow: string;
} {
  const part = part_ as any;
  const DetailSerien = part?.meta?.DetailSerie;
  const Workflow = part?.isElement
    ? part.isTor
      ? part.isSchiebetor
        ? "WORKFLOW_Schiebetor"
        : part.isDreifachtor
          ? "WORKFLOW_Dreifachtor"
          : part.isDoppeltor
            ? part.isEAntrieb
              ? "WORKFLOW_Doppeltor_EAntrieb"
              : "WORKFLOW_Doppeltor"
            : "WORKFLOW_Einzeltor"
      : "WORKFLOW_Zaunelement"
    : null;
  return { DetailSerien, Workflow };
}

/**
 * Gibt die Abstände zwischen gleichen Elementen zurück, entspricht ansonsten getElementZuElementDeltas
 * @param element
 */
function getElementZuElementDeltasFürGleicheElemente(element: ZElement) {
  const arrangement = element.arrangement;
  return getZwischenabstand(toAbschnittsteil(element), toAbschnittsteil(element), arrangement);
}

export function getSegmentEndenAusgleichForEcke(ecke: Ecke): number {
  return ecke && !ecke.anMauer && ecke.pfosten ? ecke.pfosten.breite * -0.5 : 0;
}

/**
 * Findet das passende Gegenstück zu einem Dreifachtor (Gebe Doppeltor, kriege Einzeltor und andersrum)
 * @param tor
 */
export function getDreifachtorPartnerElement(tor: ZElement): ZElement {
  const nachbarn = tor.getSegmentNachbarn();
  return nachbarn.find((nachbarElement) => (nachbarElement as ZElement)?.isDreifachtor) as ZElement;
}

export interface AngrenzendeTeile {
  umprofil1: any;
  pfosten: Pfosten;
  umprofil2: any;
  // element und empty schließen sich gegenseitig aus
  element: ZElement;
  empty: Empty;
  // ecke ist gesetzt, wenn ein Eckpfosten oder Eckadapter gesetzt ist oder allgemein wenn ein Teil an der Ecke steht
  // die Info, ob man an der Mauer steht, bekommt man über ecke.anMauer
  ecke: Ecke;
  eckadapter: Eckadapter;
}

export function getAngrenzendeTeile(
  startPart: ZElement | Empty,
  richtung: Richtung
): Partial<AngrenzendeTeile> {
  const next = richtung == LINKS ? (p: any) => p?.nachbarLinks : (p: any) => p?.nachbarRechts;

  let result: Partial<AngrenzendeTeile> = {};
  let part = startPart as any;
  let lastPart = part;
  let segment = null;

  const maybeSetSegment = (part) => {
    if (segment == null && part != null && !part.isPfostenAnEcke && part.segment) {
      segment = part.segment;
    }
  };

  while (true) {
    maybeSetSegment(part);
    part = next(part);
    if (part == null) {
      break;
    }
    maybeSetSegment(part);
    lastPart = part;
    if (part.isUMProfil) {
      if (result.pfosten == null) {
        result.umprofil1 = part;
      } else {
        result.umprofil2 = part;
      }
    } else if (part.isEckadapter) {
      result.eckadapter = part as Eckadapter;
    } else if (part.isPfosten) {
      result.pfosten = part as Pfosten;
    } else if (part.isElement) {
      result.element = part as ZElement;
      break;
    } else if (part.isEmpty) {
      result.empty = part as Empty;
      break;
    }
  }
  part = part ?? lastPart;

  if (part) {
    if (part.isPfostenAnEcke) {
      result.ecke = part.ecke;
    } else if (part.isUMProfil || part.isEckadapter || part.isPfosten || startPart === lastPart) {
      const side = richtung == LINKS ? 0 : 1;
      const x = segment.getX(part, side);
      const eckenX = richtung == LINKS ? 0 : segment.breite;
      const ecke = segment.ecken[side];
      const pfosten = ecke.pfosten;
      const pfostenHalbBreite = (pfosten?.breite ?? 0) / 2;
      const eckadapterDelta = segment.eckadapterDelta[side];
      const segmentendenGrenzwert = 0.01 + eckadapterDelta + pfostenHalbBreite;
      const segmentende = Math.abs(x - eckenX) < segmentendenGrenzwert;

      if (segmentende) {
        result.ecke = ecke;
      }
    }
  }

  return result;
}

export function getEntferntAngrenzendeFreiraumBreite(part: any, richtung: Richtung): number {
  while (part) {
    const { ecke, element, empty } = getAngrenzendeTeile(part, richtung);
    if (empty?.automatischeBreite) {
      return empty.breite ?? 0;
    }
    if (ecke) {
      return 0;
    }
    part = element ?? empty;
  }
  return 0;
}

export function getEntferntAngrenzendeFreiraumBreiteSumme(part: any): number {
  return (
    getEntferntAngrenzendeFreiraumBreite(part, LINKS) +
    getEntferntAngrenzendeFreiraumBreite(part, RECHTS)
  );
}

/**
 * wenn ein Element oder eine Mauer in einen Leeraum eingefügt wird, dann stellt sich die Frage,
 * wie breit das Element oder die Mauer sein muss, um den vorhandenen Platz lückenlos zu füllen.
 * Dazu muss man allerdings wissen, wie viel Platz Pfosten und eventuell UMProfile einnehmen,
 * und welches Deltaabstandsmass verwendet wird, und ob zuvor schon Pfosten / UMProfile
 * vorhanden waren.
 */
export function getIstSollZwischenabstandDelta(
  elementOrEmptyToReplace: ZElement | Empty,
  replaceWithPartOrMauer: ZElement | Empty | MElement | Abschnittsende.Mauer,
  richtung: Richtung
) {
  const ist = getZwischenabstandAnSeite(elementOrEmptyToReplace, richtung);
  const soll = getSollZwischenabstand(elementOrEmptyToReplace, replaceWithPartOrMauer, richtung);
  return ist - soll;
}

export function getIstSollZwischenabstandDeltaSum(
  elementOrEmptyToReplace: ZElement | Empty,
  replaceWithPartOrMauer: ZElement | Empty | MElement | Abschnittsende.Mauer
) {
  return (
    getIstSollZwischenabstandDelta(elementOrEmptyToReplace, replaceWithPartOrMauer, LINKS) +
    getIstSollZwischenabstandDelta(elementOrEmptyToReplace, replaceWithPartOrMauer, RECHTS)
  );
}

export enum Abschnittsende {
  Segmentende = "Segmentende",
  Empty = "Empty",
  Mauer = "Mauer",
}

export interface Abschnittsteil {
  // Abschnittsteil kann kein UMrofile oder Eckadapter sein, statt dessen wird der Pfosten genommen
  // und es wird per Flags isWinkelbereichU und isEckadapter gearbeitet
  meta: MElement | MTor | MPfosten | null;
  type: // type entsprechend meta
  | "Element"
    | "Tor"
    | "Pfosten"
    // folgende falls meta null:
    | "Mauer"
    | "Empty"
    // Segmentende bedeutet Ecke ohne Eckpfosten. Zusätzlich ist isSegmentende dann true.
    | "Segmentende";
  // nur true möglich bei Pfosten | Tor | Element
  isAufhängungAnLinkerTorseite: boolean;
  // nur true möglich falls (Pfosten | Segmentende) && isSegmentende
  isWinkelbereichU: boolean;
  // nur true möglich falls (Pfosten | Segmentende) && isSegmentende
  isEckadapter: boolean;
  // isSegmentende == nahe der Ecke, dann wird der Segmentendenausgleich ermittelt.
  // bedeutet nicht unbedingt type=="Segmentende"
  isSegmentende: boolean;
  // Marker für dieses Interface, immer true
  isAbschnittsteil: boolean;
}

// | MElement
// | (MElement & AbschnittsteilExtension)
// | MTor
// | (MTor & AbschnittsteilExtension)
// | MPfosten
// | (MPfosten & AbschnittsteilExtension)
// | AEckadapter
// | Abschnittsende;

export function toAbschnittsteil(
  part:
    | Abschnittsteil
    | Abschnittsende
    | Ecke
    | Element
    | MElement
    | MTor
    | Empty
    | Pfosten
    | MPfosten
    | MUMProfil
    | ZElement
    | null,
  {
    isAufhängungAnLinkerTorseite = false,
    isWinkelbereichU = false,
    isEckadapter = false,
    isSegmentende = false,
  } = {}
): Abschnittsteil {
  const defaults = {
    isAufhängungAnLinkerTorseite: false,
    isWinkelbereichU: false,
    isEckadapter: false,
    isSegmentende: false,
    isAbschnittsteil: true,
  };
  if (part == null) {
    return null;
  } else if ((part as Abschnittsteil).isAbschnittsteil) {
    return part as Abschnittsteil;
  } else if (
    part === Abschnittsende.Mauer ||
    part === Abschnittsende.Empty ||
    part === Abschnittsende.Segmentende
  ) {
    return {
      ...defaults,
      meta: null,
      type: part as Abschnittsende.Mauer | Abschnittsende.Empty | Abschnittsende.Segmentende,
    };
  } else if ((part as Empty).isEmpty) {
    return {
      ...defaults,
      meta: null,
      type: Abschnittsende.Empty,
    };
  } else if ((part as Ecke).isEcke) {
    if ((part as Ecke).anMauer) {
      return {
        ...defaults,
        meta: null,
        type: Abschnittsende.Mauer,
        isSegmentende: true,
      };
    }
    return {
      ...defaults,
      meta: null,
      type: Abschnittsende.Segmentende,
      isSegmentende: true,
    };
  } else if ((part as ZElement).isTor) {
    return {
      ...defaults,
      meta: (part as ZElement).meta,
      type: "Tor",
      isAufhängungAnLinkerTorseite: (part as ZElement).isAufhängungAnLinkerTorseite,
    };
  } else if (isTorFn(part as MTor)) {
    return {
      ...defaults,
      meta: part as MTor,
      type: "Tor",
      isAufhängungAnLinkerTorseite,
    };
  } else if ((part as ZElement).isElement) {
    return {
      ...defaults,
      meta: (part as ZElement).meta,
      type: "Element",
      isAufhängungAnLinkerTorseite,
    };
  } else if (isElementFn(part as MElement)) {
    return {
      ...defaults,
      meta: part as MElement,
      type: "Element",
      isAufhängungAnLinkerTorseite,
    };
  } else if ((part as Pfosten).isPfosten) {
    return {
      ...defaults,
      meta: (part as Pfosten).meta as MPfosten,
      type: "Pfosten",
      isAufhängungAnLinkerTorseite,
      isWinkelbereichU,
      isEckadapter,
      isSegmentende: isSegmentende || (part as Pfosten).isPfostenAnEcke,
    };
  } else if (isPfostenFn(part as MPfosten)) {
    return {
      ...defaults,
      meta: part as MPfosten,
      type: "Pfosten",
      isAufhängungAnLinkerTorseite,
      isWinkelbereichU,
      isEckadapter,
      isSegmentende,
    };
  } else {
    return null;
  }
}

/**
 * Ermittelt, wieviel Zwischenabstand nach einer Ersetzung eines gegebenen Teils durch ein anderes
 * sein würde, in die entsprechende Richtung zum Nachbarn
 * Der Zwischenabstand ist die Summe der Breite aller Pfosten, UMProfile, Gaps, Segmenteendenausgleich
 * zwischen Leerraum | Element und Element | Mauer | Segmentende in eine
 */
export function getSollZwischenabstand(
  elementOrEmptyToReplace: ZElement | Empty,
  replaceWithPartOrMauer: ZElement | Empty | MElement | Abschnittsende.Mauer,
  richtung: Richtung
) {
  return getSollZwischenabstandVerbose(elementOrEmptyToReplace, replaceWithPartOrMauer, richtung)
    .zwischenabstand;
}

/**
 * Ermittelt, wieviel Zwischenabstand nach einer Ersetzung eines gegebenen Teils durch ein anderes
 * sein würde, in die entsprechende Richtung zum Nachbarn
 * Der Zwischenabstand ist die Summe der Breite aller Pfosten, UMProfile, Gaps, Segmenteendenausgleich
 * zwischen Leerraum | Element und Element | MElement | Mauer | Segmentende in eine Richtung.
 */
export function getSollZwischenabstandVerbose(
  elementOrEmptyToReplace: ZElement | Empty,
  replaceWithPartOrMauer: ZElement | Empty | MElement | Abschnittsende.Mauer,
  richtung: Richtung
): ZwischenabstandVerbose {
  if (elementOrEmptyToReplace == null || replaceWithPartOrMauer == null) {
    return {
      zwischenabstand: 0,
      gap: 0,
      umprofilBreite: 0,
      gapLinks: 0,
      umprofilBreiteLinks: 0,
      pfostenBreite: 0,
      umprofilBreiteRechts: 0,
      gapRechts: 0,
      segmentEndenAusgleich: 0,
      eckadapterDelta: 0,
      richtung,
    };
  }

  if (
    !((elementOrEmptyToReplace as ZElement).isElement || (elementOrEmptyToReplace as Empty).isEmpty)
  ) {
    throw new Error("paramter elementOrEmptyToReplace must be of class ZElement or Empty");
  }

  const replaceWithIsMeta = (replaceWithPartOrMauer as MKeyTable).Key !== undefined;

  if (
    !(
      (replaceWithPartOrMauer as ZElement).isElement ||
      (replaceWithPartOrMauer as Empty).isEmpty ||
      replaceWithIsMeta ||
      replaceWithPartOrMauer == Abschnittsende.Mauer
    )
  ) {
    throw new Error(
      "paramter replaceWithPartOrMauer must be of class ZElement or MElement or Empty or it must be the string 'Mauer'"
    );
  }

  let { eckadapter, pfosten, element, empty, ecke } = getAngrenzendeTeile(
    elementOrEmptyToReplace,
    richtung
  );

  const isAufhängungAnLinkerTorseite =
    (replaceWithPartOrMauer as ZElement).isAufhängungAnLinkerTorseite ??
    (elementOrEmptyToReplace as ZElement).isAufhängungAnLinkerTorseite ??
    false;

  let isWinkelbereichU = false;
  let isEckadapter = false;
  if (pfosten && (elementOrEmptyToReplace as ZElement)?.isElement) {
    const angle = getPfostenWinkel(pfosten, elementOrEmptyToReplace as ZElement).angle;
    const intervalType = getAngleIntervalType(pfosten, angle);
    isWinkelbereichU = intervalType === "U";
    isEckadapter = intervalType === "E";
  } else {
    isEckadapter = eckadapter != null;
  }

  const isSegmentende = ecke != null;
  const teilAnSeiteQuelle =
    ((isSegmentende || isEckadapter) && pfosten?.meta) || element || ecke || empty || pfosten?.meta;

  const teilAnSeite = toAbschnittsteil(teilAnSeiteQuelle, {
    isAufhängungAnLinkerTorseite,
    isWinkelbereichU,
    isEckadapter,
    isSegmentende,
  });
  const teilReplacement = toAbschnittsteil(replaceWithPartOrMauer, {
    isAufhängungAnLinkerTorseite,
    isWinkelbereichU,
    isEckadapter,
    isSegmentende,
  });

  const [teilLinks, teilRechts] =
    richtung == LINKS ? [teilAnSeite, teilReplacement] : [teilReplacement, teilAnSeite];

  const arrangement = elementOrEmptyToReplace.arrangement;

  const result = {
    // elementOrEmptyToReplace,
    // replaceWithPartOrMauer,
    // teilAnSeite,
    // teilReplacement,
    ...getZwischenabstandVerbose(teilLinks, teilRechts, arrangement),
    richtung,
  };

  // console.log("getSollZwischenabstandVerbose", result);

  return result;
}

/**
 * Ermittelt den Zwischenabstand an der linken oder rechten Seite eines Teils zum Nachbarn.
 * Der Zwischenabstand ist die Summe der Breite aller Pfosten, UMProfile, Gaps, Segmenteendenausgleich
 * zwischen Leerraum | Element und Element | Mauer | Segmentende in eine
 */
export function getZwischenabstandAnSeite(emptyOrElement: ZElement | Empty, richtung: Richtung) {
  return getZwischenabstandAnSeiteVerbose(emptyOrElement, richtung).zwischenabstand;
}

// export function findEcke(emptyOrElement: any, richtung: Richtung) {
//   if (emptyOrElement == null) {
//     return null;
//   }
//   if (emptyOrElement.isPfostenAnEcke) {
//     return emptyOrElement.ecke;
//   }
//   return emptyOrElement.segment.ecken[richtung == Richtung.LINKS ? 0 : 1];
// }

export function getZwischenabstandAnSeiteVerbose(
  emptyOrElement: ZElement | Empty,
  richtung: Richtung
): {
  richtung: Richtung;
  zwischenabstand: number;
  gap: number;
  umprofilBreite: number;
  gapLinks: number;
  umprofilBreiteLinks: number;
  pfostenBreite: number;
  umprofilBreiteRechts: number;
  gapRechts: number;
  segmentEndenAusgleich: number;
  eckadapterDelta: number;
} {
  if (emptyOrElement == null) {
    return {
      richtung,
      zwischenabstand: 0,
      gap: 0,
      umprofilBreite: 0,
      gapLinks: 0,
      umprofilBreiteLinks: 0,
      pfostenBreite: 0,
      umprofilBreiteRechts: 0,
      gapRechts: 0,
      segmentEndenAusgleich: 0,
      eckadapterDelta: 0,
    };
  }

  if (!((emptyOrElement as ZElement).isElement || (emptyOrElement as Empty).isEmpty)) {
    throw new Error(
      "paramter elementOrEmptyToReplace must be of class ZElement or Empty but is " +
        Commons.typeName(emptyOrElement)
    );
  }

  const { umprofil1, pfosten, umprofil2, element, empty, eckadapter, ecke } = getAngrenzendeTeile(
    emptyOrElement,
    richtung
  );

  const gapFn = (part, richtung) => (richtung == LINKS ? part?.gapLinks : part?.gapRechts);

  const gap1 = gapFn(emptyOrElement, richtung) ?? 0;
  const umProfilBreite1 = umprofil1?.breite ?? 0;
  const pfostenBreite = pfosten?.breite ?? 0;
  const umProfilBreite2 = umprofil2?.breite ?? 0;
  const gap2 = element != null ? gapFn(element, richtung == LINKS ? RECHTS : LINKS) ?? 0 : 0;

  const pfostenSegmentendenAusgleich = (pfosten?.breite ?? 0) * -0.5;
  const istSegmentEnde = ecke != null;
  const segmentEndenAusgleich =
    (emptyOrElement as ZElement).isElement && istSegmentEnde ? pfostenSegmentendenAusgleich : 0;

  // Zuvor wurde der segmentEndenAusgleich immer berechnet - für alle Elemente des Segmentes,
  // jetzt nur noch für das Element am Eckpfosten.

  // const ecke = findEcke(emptyOrElement, richtung);
  // const eckPfostenHalbBreite = (ecke?.pfosten?.breite ?? pfostenbreiteCirca) / 2;
  // const segmentEndenAusgleich = emptyOrElement.isElement ? -eckPfostenHalbBreite : 0;

  const eckadapterDelta =
    ((emptyOrElement as ZElement).isElement && eckadapter?.eckadapterDelta) || 0;

  const zwischenabstand =
    gap1 +
    umProfilBreite1 +
    pfostenBreite +
    umProfilBreite2 +
    gap2 +
    eckadapterDelta +
    segmentEndenAusgleich;

  return {
    richtung,
    zwischenabstand,
    gap: gap1 + gap2,
    umprofilBreite: umProfilBreite1 + umProfilBreite2,
    gapLinks: richtung == LINKS ? gap2 : gap1,
    umprofilBreiteLinks: richtung == LINKS ? umProfilBreite2 : umProfilBreite1,
    pfostenBreite,
    umprofilBreiteRechts: richtung == LINKS ? umProfilBreite1 : umProfilBreite2,
    gapRechts: richtung == LINKS ? gap1 : gap2,
    eckadapterDelta,
    segmentEndenAusgleich,
  };
}

/**
 * gibt für zwei angrenzende Abschnittsteil den durch Pfosten, UMProfile und Gaps entstehende Zwischenabstand zurück.
 * Gilt für MElement statt Element.
 */
export function getZwischenabstand(
  teilLinks: Abschnittsteil,
  teilRechts: Abschnittsteil,
  arrangement: Arrangement
): number {
  return getZwischenabstandVerbose(teilLinks, teilRechts, arrangement).zwischenabstand;
}

export interface ZwischenabstandVerbose {
  richtung: string;
  zwischenabstand: number;
  gap: number;
  umprofilBreite: number;
  gapLinks: number;
  umprofilBreiteLinks: number;
  pfostenBreite: number;
  umprofilBreiteRechts: number;
  gapRechts: number;
  segmentEndenAusgleich: number;
  eckadapterDelta: number;
}

function getPfostenMetaIntersection(teilLinks: MElement | null, teilRechts: MElement | null) {
  const pfostenDerElemente = [teilLinks, teilRechts]
    .map(
      (element) =>
        filterPathIntersectionCached("elementFunctions.ts:1296")(
          element,
          (e: PTor) => e.Pfostentyp.Pfosten
        ) as MPfosten[]
    )
    .filter((x) => x.length > 0);

  let pfostenMetas = _.intersection(...pfostenDerElemente) as MPfosten[];

  const torIndex = [teilLinks, teilRechts].findIndex(isTorFn);

  if (pfostenMetas.length == 0 && torIndex >= 0) {
    pfostenMetas = pfostenDerElemente[torIndex];
  }
  return pfostenMetas;
}

/**
 * Gibt für zwei angrenzende Abschnittsteil den durch Pfosten, UMProfile und Gaps entstehende Zwischenabstand zurück.
 */
export function getZwischenabstandVerbose(
  teilLinks: Abschnittsteil,
  teilRechts: Abschnittsteil,
  arrangement: Arrangement
): ZwischenabstandVerbose {
  const isAbschnittsteilType = (teil) =>
    teil !== null &&
    (teil.meta == null || teil.meta.Key != null) &&
    ["Element", "Tor", "Pfosten", "Mauer", "Empty", "Segmentende"].includes(teil.type);
  if (!isAbschnittsteilType(teilLinks)) {
    throw new Error("paramter teilLinks must be of type Abschnittsteil");
  }
  if (!isAbschnittsteilType(teilRechts)) {
    throw new Error("paramter teilRechts must be of type Abschnittsteil");
  }

  const istPfosten = (teil: Abschnittsteil) => teil.type === "Pfosten";
  const istElement = (teil: Abschnittsteil) => teil.type === "Element" || teil.type === "Tor";
  const istTor = (teil: Abschnittsteil) => teil.type === "Tor";
  const istElementLinks = istElement(teilLinks);
  const istElementRechts = istElement(teilRechts);

  if (!istElementLinks && !istElementRechts) {
    return {
      richtung: null,
      zwischenabstand: 0,
      gap: 0,
      umprofilBreite: 0,
      gapLinks: 0,
      umprofilBreiteLinks: 0,
      pfostenBreite: 0,
      umprofilBreiteRechts: 0,
      gapRechts: 0,
      segmentEndenAusgleich: 0,
      eckadapterDelta: 0,
    };
  }

  // solange sich Pfosten im Abstand und Breite nicht anhand der Höhe unterscheiden, ist das hier ok.
  // Ansonsten müßte man das MTorMitAufhängung um elementHöheAmPfosten erweitern, durchreichen und hier mit max auslesen
  const gesuchtePfostenHöhe = 1.8;

  const elementMetaLinks = istElementLinks ? (teilLinks.meta as MElement) : null;
  const elementMetaRechts = istElementRechts ? (teilRechts.meta as MElement) : null;
  const [isTorLinks, isTorRechts] = [teilLinks, teilRechts].map(istTor);
  const torTeil = [teilLinks, teilRechts].find(istTor);
  const torMeta = torTeil?.meta as MTor;
  let pfostenMetas = getPfostenMetaIntersection(elementMetaLinks, elementMetaRechts);

  const pfostenartAuswahlwert =
    arrangement.pfostenartAuswahlwert ??
    APP.richMetadata.getAuswahlWertVoreinstellungForKey("Pfostenart");

  // die Parameter hier sind wichtig, wenn sie die Entscheidung betreffen, wie breit
  // der Pfosten sein wird, und ob ein UMProfil / Montageplatte / Anschlagprofil notwendig wird.
  // Deswegen können einige Parameter dummywerte sein.
  const pfostenMetaParameter: PfostenMetaParameter = {
    pfostenfarbeAuswahlwert: arrangement.pfostenfarbeAuswahlwert,
    anschlagprofilfarbeAuswahlwert: arrangement.anschlagprofilfarbeAuswahlwert,
    pfostenartAuswahlwert,
    bodenbefestigungAuswahlwert: arrangement.bodenbefestigungAuswahlwert,
    elementhalterAuswahlwert: arrangement.elementhalterAuswahlwert,
    ausgleichGefälle: false,
    meta: null,
    pfostenMetas,
    elemente: [elementMetaLinks, elementMetaRechts],
    montageadapterAuswahlwert:
      APP.richMetadata.getAuswahlWertVoreinstellungForKey("Montageadapter"),
    verbundankerAuswahlwert: APP.richMetadata.getAuswahlWertVoreinstellungForKey("Verbundanker"),
    ummantelungAuswahlwert: APP.richMetadata.getAuswahlWertVoreinstellungForKey("Ummantelung"),
    pfostenkappeAuswahlwert: APP.richMetadata.getAuswahlWertVoreinstellungForKey("Pfostenkappe"),
    ankerAuswahlwert: null,
    angles: [
      { angle: 0, flacheSeiteNotwendig: isTorLinks, type: undefined, element: undefined },
      { angle: Math.PI, flacheSeiteNotwendig: isTorRechts, type: undefined, element: undefined },
    ],
    gesuchtePfostenHöhe,
    isMaxElementHöhe: false,
    isTor: torTeil != null,
    mauer: teilLinks.type === Abschnittsende.Mauer || teilRechts.type === Abschnittsende.Mauer,
    toraufhängung:
      hasAbschnittsteilAufhängungAnPfosten(teilLinks, RECHTS) ||
      hasAbschnittsteilAufhängungAnPfosten(teilRechts, LINKS),
    torschloss:
      hasAbschnittsteilSchlossAnPfosten(teilLinks, RECHTS) ||
      hasAbschnittsteilSchlossAnPfosten(teilRechts, LINKS),
    aufbautyp: (torMeta ?? elementMetaLinks ?? elementMetaRechts)?.Aufbautyp,
    anschlagprofilAuswahlwert:
      APP.richMetadata.getAuswahlWertVoreinstellungOrFirstForKey("Anschlagprofil"),
  };

  if (istElementLinks && istElementRechts) {
    const {
      isUMProfil,
      meta: pfostenMeta,
      breite: pfostenBreite,
      anschlagprofilfarbeAuswahlwert,
    } = getPfostenMetaParameter(pfostenMetaParameter);

    if (isUMProfil) {
      throw new Error(`UMProfil statt Pfosten ist zwischen Elementen nicht möglich`);
    }
    const umProfilMetaParameter = {
      pfostenfarbe: pfostenMeta.Pfostenfarbe?.[0]?.Auswahlwert,
      // anschlagprofilfarbeAuswahlwert,
      pfostentyp: pfostenMeta.Pfostentyp,
      gesuchtePfostenHöhe,
      mauer: false,
    };
    const umprofilMetaLinks = APP.richMetadata.getUMProfilMeta({
      ...umProfilMetaParameter,
      pfostenartAuswahlwert: isTorLinks ? null : pfostenartAuswahlwert,
      anschlagprofilfarbeAuswahlwert: isTorLinks
        ? null
        : arrangement.anschlagprofilfarbeAuswahlwert,
      toraufhängung: hasAbschnittsteilAufhängungAnPfosten(teilLinks, RECHTS),
      torschloss: hasAbschnittsteilSchlossAnPfosten(teilLinks, RECHTS),
      aufbautyp: elementMetaLinks?.Aufbautyp,
      isElementhalter: isElementhalterFn(elementMetaLinks),
    });
    const umprofilMetaRechts = APP.richMetadata.getUMProfilMeta({
      ...umProfilMetaParameter,
      pfostenartAuswahlwert: isTorRechts ? null : pfostenartAuswahlwert,
      anschlagprofilfarbeAuswahlwert: isTorRechts
        ? null
        : arrangement.anschlagprofilfarbeAuswahlwert,
      toraufhängung: hasAbschnittsteilAufhängungAnPfosten(teilRechts, LINKS),
      torschloss: hasAbschnittsteilSchlossAnPfosten(teilRechts, LINKS),
      aufbautyp: elementMetaRechts?.Aufbautyp,
      isElementhalter: isElementhalterFn(elementMetaRechts),
    });
    const umprofilBreiteLinks = umprofilMetaLinks?.Standard?.x ?? 0;
    const umprofilBreiteRechts = umprofilMetaRechts?.Standard?.x ?? 0;
    const gapLinks = APP.richMetadata.getDeltaabstandsmass(
      elementMetaLinks,
      pfostenMeta,
      undefined,
      umprofilMetaLinks
    );
    const gapRechts = APP.richMetadata.getDeltaabstandsmass(
      elementMetaRechts,
      pfostenMeta,
      undefined,
      umprofilMetaRechts
    );
    const zwischenabstand =
      gapLinks + umprofilBreiteLinks + pfostenBreite + umprofilBreiteRechts + gapRechts;
    return {
      richtung: null,
      zwischenabstand,
      gap: gapLinks + gapRechts,
      umprofilBreite: umprofilBreiteLinks + umprofilBreiteRechts,
      gapLinks,
      umprofilBreiteLinks,
      pfostenBreite,
      umprofilBreiteRechts,
      gapRechts,
      segmentEndenAusgleich: 0,
      eckadapterDelta: 0,
    };
  }

  // Element an Leerraum | Mauer | Segmentende
  const elementMeta = istElementLinks ? elementMetaLinks : elementMetaRechts;
  const ende = istElementLinks ? teilRechts : teilLinks;

  let pfostenMeta: MPfosten;
  let umprofilMeta: MUMProfil;
  let pfostenBreite = 0;
  let umprofilBreite = 0;
  let isUMProfil: boolean;

  if (istPfosten(ende)) {
    // hier haben wir einen Eckpfosten, der ein Torpfosten sein kann, welcher wiederum ein UMprofil notwendig machen kann
    pfostenMeta = ende.meta as MPfosten;
    pfostenBreite = pfostenMeta.Pfostenmass.Breite;
    isUMProfil = false;
  } else {
    let meta: MKeyTable;
    let breite: number;
    ({ isUMProfil, meta, breite } = getPfostenMetaParameter(pfostenMetaParameter));
    if (isUMProfil) {
      [umprofilMeta, umprofilBreite] = [meta as MUMProfil, breite];
    } else {
      [pfostenMeta, pfostenBreite] = [meta as MPfosten, breite];
    }
  }

  // hier fehlen noch die Fälle
  // Eckadapter + Element -> UMProfil
  // Pfosten + Element -> UMProfil

  if (isUMProfil && ende.type !== Abschnittsende.Mauer) {
    throw new Error(`UMProfil statt Pfosten ist für Element an ${ende} nicht möglich`);
  }

  if (!isUMProfil) {
    const richtungDesEndes = istElementLinks ? RECHTS : LINKS;
    const umprofilParameter: UMProfilMetaParamter = {
      pfostenfarbe: pfostenMeta.Pfostenfarbe?.[0],
      pfostentyp: pfostenMeta.Pfostentyp,
      gesuchtePfostenHöhe: pfostenMetaParameter.gesuchtePfostenHöhe,
      pfostenartAuswahlwert: torTeil != null ? null : pfostenartAuswahlwert,
      toraufhängung: hasAbschnittsteilAufhängungAnPfosten(torTeil, richtungDesEndes),
      torschloss: hasAbschnittsteilSchlossAnPfosten(torTeil, richtungDesEndes),
      mauer: ende.type === Abschnittsende.Mauer,
      aufbautyp: elementMeta.Aufbautyp,
      isElementhalter: isElementhalterFn(istElementLinks ? elementMetaLinks : elementMetaRechts),
      isEckadapter: ende.isEckadapter,
      isWinkelbereichU: ende.isWinkelbereichU,
    };
    umprofilMeta = APP.richMetadata.getUMProfilMeta(umprofilParameter);
    umprofilBreite = umprofilMeta?.Standard?.x ?? 0;
  }

  let eckadapterMeta = undefined;
  let eckadapterDelta = 0;
  if (ende.isEckadapter) {
    // hier eigenartiger Fall: pfostenMeta hat kein Eckadapter..
    ({ eckadapterMeta, eckadapterDelta } = getEckadapterInfo(pfostenMeta));
  }

  const gap = APP.richMetadata.getDeltaabstandsmass(
    elementMeta,
    pfostenMeta,
    undefined,
    umprofilMeta,
    eckadapterMeta
  );

  const segmentEndenAusgleich = ende.isSegmentende ? pfostenBreite * -0.5 : 0;

  // console.log("****", { segmentEndenAusgleich, ende, pfostenBreite, pfostenMetaParameter });

  const zwischenabstand =
    pfostenBreite + umprofilBreite + gap + eckadapterDelta + segmentEndenAusgleich;

  return {
    richtung: null,
    zwischenabstand,
    gap,
    umprofilBreite,
    gapLinks: istElementLinks ? gap : 0,
    umprofilBreiteLinks: istElementLinks ? umprofilBreite : 0,
    pfostenBreite,
    umprofilBreiteRechts: istElementRechts ? umprofilBreite : 0,
    gapRechts: istElementRechts ? gap : 0,
    segmentEndenAusgleich,
    eckadapterDelta,
  };
}

/**
 * @param elementOrEmptyToReplace
 * @param replaceWithPartOrMauer
 * @param wunschbreite optional, falls angegeben, wird evtl. angrenzender Leeraum
 * berücksichtigt und übersprungen
 */
export const getSollZwischenabstandSumme = (
  elementOrEmptyToReplace: ZElement | Empty,
  replaceWithPartOrMauer: ZElement | Empty | MElement | Abschnittsende.Mauer,
  wunschbreite = 0
): number => {
  if ((elementOrEmptyToReplace.parent as any).isArrangement) {
    // Nicht Teil eines Segmentes - z.b. Eckpfosten, oder Drag-Operation
    return 0;
  }
  let restbreite = wunschbreite - elementOrEmptyToReplace.breite;
  const epsilon = 0.001;
  if (restbreite < epsilon) {
    // Einfache Berechnung, falls Leerräume nicht berücksichtigt werden müssen
    return (
      getSollZwischenabstand(elementOrEmptyToReplace, replaceWithPartOrMauer, LINKS) +
      getSollZwischenabstand(elementOrEmptyToReplace, replaceWithPartOrMauer, RECHTS)
    );
  }
  // für Elemente (vor allem Doppeltore), die nach dem Austausch in einen Leerraum ragen würden,
  // ist der Ausgangspunkt nicht das Element, sondern der Leerraum und dessen Nachbar
  const richtungsPart = {
    [LINKS]: elementOrEmptyToReplace,
    [RECHTS]: elementOrEmptyToReplace,
  };
  const segment = elementOrEmptyToReplace.segment;
  // statt element und empty -.breite wird das x0-x1 Interval berechnet, um Pfosten und Zwischenabstände zu berücksichtigen
  const xOf = (part, richtung: Richtung) => segment.getX(part, richtung == LINKS ? 0 : 1);
  const xInterval = {
    [LINKS]: xOf(elementOrEmptyToReplace, LINKS),
    [RECHTS]: xOf(elementOrEmptyToReplace, RECHTS),
  };
  const xSize = () => xInterval[RECHTS] - xInterval[LINKS];
  // Doppeltore dehen sich erst nach Rechts, dann Links aus
  for (const richtung of [RECHTS, LINKS]) {
    if (restbreite < epsilon) {
      break;
    }
    const { empty } = getAngrenzendeTeile(elementOrEmptyToReplace, richtung);
    if (empty == null) {
      continue;
    }
    richtungsPart[richtung] = empty;
    xInterval[richtung] = xOf(empty, richtung);
    restbreite = wunschbreite - xSize();
  }
  const sollZwischenabstandSumme =
    getSollZwischenabstand(richtungsPart[LINKS], replaceWithPartOrMauer, LINKS) +
    getSollZwischenabstand(richtungsPart[RECHTS], replaceWithPartOrMauer, RECHTS);
  return sollZwischenabstandSumme;
};

export const getIstZwischenabstandSumme = (part: any): number =>
  part.parent.isArrangement
    ? 0
    : getZwischenabstandAnSeite(part, LINKS) + getZwischenabstandAnSeite(part, RECHTS);

export const isDoppeltorFn = (elementMeta: MKeyTable): boolean => (elementMeta as MTor)?.Doppeltor;

export const isSenkrechtDoppeltorFn = (elementMeta: MKeyTable): boolean =>
  isDoppeltorFn(elementMeta) && (elementMeta as MTor)?.Aufbautyp?.Key == "SystemSenkrechtprofile";

export const hasAufhängungAnPfosten = (
  elementMeta: MElement,
  anschlagLinks: boolean,
  richtung: Richtung
): boolean =>
  isTorFn(elementMeta) && (isDoppeltorFn(elementMeta) || anschlagLinks === (LINKS === richtung));

export const hasSchlossAnPfosten = (
  elementMeta: MElement,
  anschlagLinks: boolean,
  richtung: Richtung
): boolean =>
  isTorFn(elementMeta) && !isDoppeltorFn(elementMeta) && anschlagLinks === (RECHTS === richtung);

export const hasAbschnittsteilAufhängungAnPfosten = (
  teil: Abschnittsteil,
  richtung: Richtung
): boolean =>
  teil?.type == "Tor" &&
  (isDoppeltorFn(teil.meta) || teil.isAufhängungAnLinkerTorseite === (LINKS === richtung));

export const hasAbschnittsteilSchlossAnPfosten = (
  teil: Abschnittsteil,
  richtung: Richtung
): boolean =>
  teil?.type == "Tor" &&
  !isDoppeltorFn(teil.meta) &&
  teil["isAufhängungAnLinkerTorseite"] === (RECHTS === richtung);

export const isTorschließerMöglichFn = (
  elementMeta: MElement,
  gehflügelbreite: Number,
  isGehflügelAufhängungAnMauer: boolean
): boolean => {
  return (
    isTorFn(elementMeta) &&
    !isGehflügelAufhängungAnMauer &&
    gehflügelbreite <= APP.settings.MaximalbreiteFlügelFürTorschließer
  );
};

export const isToröffnerMöglichFn = (elementMeta: MElement, isAnschlagprofil: boolean): boolean => {
  return isTorFn(elementMeta) && !isAnschlagprofil;
};

export const isElementhalterFn = (elementMeta: MElement) => elementMeta?.Elementhalter != null;

/**
 * Gibt für eine gegebene senkrechte Form die normale Form zurück.
 * Normale Formen (oder wenn es keine passende gibt) werden selbst zurückgegeben
 * @param form
 */
export const getNormaleFormVonSenkrechtFormOderSelbst = (form: MObjekt): MObjekt => {
  const newFormKey = form.Key.substringBefore("_Senkrecht");
  const newForm = APP.richMetadata.findForm(newFormKey);
  return newForm || form;
};

/**
 * Sucht für eine Form die entsprechend passende Senkrechte Form
 * Gibt es keine, so wird null zurückgegeben
 * @param form
 */
export const getSenkrechtFormVonNormalerForm = (form: MObjekt): MObjekt => {
  return APP.richMetadata.findForm(form.Key + "_Senkrecht");
};

/**
 * Prüft, ob eine Form ein Schiebetor ist – inklusive der Senkrecht-Variante
 * "Schiebetor_Senkrecht", die auf die normale Form "Schiebetor" zurückgeführt wird.
 * So bleibt die Schiebetor-Erkennung stabil, wenn ein Schiebetor mit
 * Aufbautyp SystemSenkrechtprofile (eigene Form _Senkrecht) konfiguriert wird.
 */
export const isSchiebetorForm = (form?: MObjekt | null): boolean =>
  form != null && getNormaleFormVonSenkrechtFormOderSelbst(form).Key == "Schiebetor";

/**
 * ermittelt zu einer Form und Füllung eine möglichst gute StandardSize
 * @param form
 * @param füllungAuswahlwert
 */
export function getStandardSize({
  form,
  füllungAuswahlwert,
}: {
  form: MObjekt;
  füllungAuswahlwert: MAuswahlwert;
}) {
  const massMetas = filterPathIntersectionCached("elementFunctions.ts:1919")(
    form,
    (p: PObjekt) => p.Mass,
    füllungAuswahlwert,
    (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt).Mass
  ) as MMass[];
  const [fertigung, andere] = Commons.partition(massMetas, (m) => m.Variabel === "fertigung");
  const score = (mass: MMass) => mass.Standard.length();
  const maxStandardMass = _.maxBy(andere, score) ?? _.maxBy(fertigung, score);
  return maxStandardMass?.Standard;
}

export const isBreiteFlügelFürEAntrieb = (flügelSizes: Vector2[]) => {
  const minimalbreite = APP.settings.MinimalbreiteFlügelFürEAntrieb;
  return flügelSizes.every((size) => size.x >= minimalbreite);
};

export const isEantriebAuswahlMöglich = (elementMeta: MElement) => {
  return elementMeta.Mass.EAntriebAuswahlMöglich;
};

/**
 * Gibt alle mit einem Part bezüglich des Snaps zusammenhängende Elemente oder fixe Leerräume zurück,
 * d.h. die auf diesem Part hinsnappen und von seiner Position abhängen.
 */
export function getSnapDependentParts(parts: Part[], part: Part): Part[] {
  const elementIndex = parts.indexOf(part);
  const dependentParts: Part[] = [part];
  let leftIndex = elementIndex - 1;
  while (leftIndex >= 0 && parts[leftIndex].snap === 1) {
    dependentParts.unshift(parts[leftIndex]);
    leftIndex--;
  }
  let rightIndex = elementIndex + 1;
  while (rightIndex < parts.length && parts[rightIndex].snap === -1) {
    dependentParts.push(parts[rightIndex]);
    rightIndex++;
  }
  return dependentParts;
}

export function getProfilStapelHöhe(profilMeta: MObjekt) {
  const modelMaterialRelation = getMMR(profilMeta);
  const modelSize = APP.modelsHelper.getBoundingBox(modelMaterialRelation).getSize(v3());
  const profilhöhe = modelSize.y;
  const profilabstand = modelMaterialRelation.Model.Profilabstand;
  const stapelhöhe = profilhöhe - profilabstand;
  return { profilabstand, profilhöhe, stapelhöhe };
}
